import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import confetti from 'canvas-confetti';
import { Card, CardContent, CardHeader } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Label } from './ui/label';
import { 
  Calendar,
  ChevronLeft,
  ChevronRight,
  Zap,
  Plus,
  Clock,
  Users,
  Package,
  Check,
  X,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  LayoutGrid,
  List as ListIcon,
  Upload,
  Filter,
  AlertCircle,
  MoreVertical,
  ChevronDown,
  CheckSquare,
  Sparkles,
  Loader2,
  Rocket,
  User,
  Activity,
  Settings,
  Phone,
  Edit,
  Trash2,
  Link2
} from 'lucide-react';
import { LoadingSpinner } from './LoadingSpinner';
import { useSettings } from '../contexts/SettingsContext';
import { formatTime, formatDate } from '../utils/formatters';
import { toast } from 'sonner';
import AutoSchedulerWarningsDialog from './AutoSchedulerWarningsDialog';
import BulkImportDialog from './BulkImportDialog';
import { Input } from './ui/input';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';
import { Calendar as CalendarPicker } from './ui/calendar';
import { format } from 'date-fns';
import DashboardPageLayout from './DashboardPageLayout';
import PageGuide from './PageGuide';
import { GUIDE_CONTENT } from '../config/guideContent';
import CardListViewToggle from './CardListViewToggle';
import StandardModal from './StandardModal';
import TimeSlotSelector from './TimeSlotSelector';
import { PrimaryButton, SecondaryButton } from './Buttons';
import TeamMemberSelector from './TeamMemberSelector';
import AssetSelector from './AssetSelector';
import RoleSelector from './RoleSelector';
import { useRoles } from '../hooks/useData';
import { Textarea } from './ui/textarea';
import MassActionToolbar from './MassActionToolbar';
import EditBookingModal from './EditBookingModal';
import AddBookingWizard from './AddBookingWizard';
import { getTimezoneAbbr } from '../utils/timezoneUtils';
import { DEFAULT_TIMEZONE } from '../utils/timezones';

const CalendarScheduler = ({ onRefresh, readOnly = false, teamMemberId = null, showMyBookingsToggle = false, onDemoClick, demoLoading = false }) => {
  const { timeFormat, dateFormat } = useSettings();
  const { roles } = useRoles(); // Add roles
  
  // Helper function to format customer name to "First L."
  const formatCustomerName = (fullName) => {
    if (!fullName) return '';
    const names = fullName.split(' ');
    const firstName = names[0];
    const lastInitial = names[names.length - 1]?.[0] || '';
    return lastInitial ? `${firstName} ${lastInitial}.` : firstName;
  };
  const [viewMode, setViewMode] = useState('calendar'); // calendar, list, card
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [trips, setBookings] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assets, setAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedDate, setSelectedDate] = useState(null);
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [selectedDayBookings, setSelectedDayBookings] = useState([]);
  const [sortField, setSortField] = useState('date');
  const [sortDirection, setSortDirection] = useState('asc');
  const [warningsDialogOpen, setWarningsDialogOpen] = useState(false);
  const [schedulerResult, setSchedulerResult] = useState(null);
  const [tripAvailability, setBookingAvailability] = useState({}); // Store availability info for trips
  const [selectedTeamMemberFilters, setSelectedTeamMemberFilters] = useState([]); // Filter trips by teamMember in day modal
  
  // Date range for list/card views
  const [dateRangePreset, setDateRangePreset] = useState('next7'); // today, next7, next30, custom
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [showCustomDatePicker, setShowCustomDatePicker] = useState(false);
  
  // "See My Bookings" toggle for teamMember view
  const [showMyBookingsOnly, setShowMyBookingsOnly] = useState(showMyBookingsToggle);
  
  // Single trip edit modal
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editTeamMemberId, setEditTeamMemberId] = useState('');
  const [editAssetId, setEditAssetId] = useState('');
  
  // Add Booking modal
  const [addBookingModalOpen, setAddBookingModalOpen] = useState(false);
  const [newBooking, setNewBooking] = useState({
    date: '',
    startTime: '',
    endTime: '',
    duration: '',
    groupSize: '',
    teamMemberId: '',
    assetId: '',
    notes: '',
    teamAssignments: [], // [{teamId, teamName, roleId, roleName}]
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    paymentStatus: 'unpaid', // unpaid, paid, ready_for_card
    totalAmount: 0,
    paymentMethod: '' // cash, card, stripe, manual
  });
  
  // Bulk Import modal
  const [bulkImportOpen, setBulkImportOpen] = useState(false);
  
  // Status filter
  const [statusFilter, setStatusFilter] = useState('all'); // all, unassigned, partial, assigned

  // Health Check modal
  const [healthModalOpen, setHealthModalOpen] = useState(false);
  const [healthData, setHealthData] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);

  // Mass actions state
  const [selectedBookingIds, setSelectedBookingIds] = useState([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [tripToDelete, setBookingToDelete] = useState(null); // For single trip deletion
  const [massActionMode, setMassActionMode] = useState(false);
  const [showResourceOverview, setShowResourceOverview] = useState(false); // Resource overview collapse
  const [expandedBookingDetails, setExpandedBookingDetails] = useState({}); // Track which trip's customer details are expanded
  const [copiedBookingId, setCopiedBookingId] = useState(null); // Track which trip link was just copied
  const [showWaiverDetailsModal, setShowWaiverDetailsModal] = useState(false);
  const [selectedWaiverBooking, setSelectedWaiverBooking] = useState(null);
  const [updateTimeouts, setUpdateTimeouts] = useState({}); // Debounce trip updates
  const [emergencyPhone, setEmergencyPhone] = useState(
    localStorage.getItem('orgEmergencyPhone') || '(512) 555-5555'
  );

  // Shimmer effect state
  const [shimmerActive, setShimmerActive] = useState(false);

  // Auto-scheduler confirmation modal state
  const [showSchedulerConfirm, setShowSchedulerConfirm] = useState(false);
  const [schedulerPreview, setSchedulerPreview] = useState(null);
  const [isScheduling, setIsScheduling] = useState(false);
  
  
  // Actions popover state
  const [actionsPopoverOpen, setActionsPopoverOpen] = useState(false);

  // Organization timezone (source of truth from settings)
  const orgTimezone = localStorage.getItem('orgTimezone') || DEFAULT_TIMEZONE;
  const getTimezoneAbbreviation = (tz) => {
    return getTimezoneAbbr(tz);
  };

  // Initialize default date range (Next 7 Days) on mount
  useEffect(() => {
    applyDateRangePreset('next7');
  }, []);

  // Shimmer effect every 18 seconds
  useEffect(() => {
    const shimmerInterval = setInterval(() => {
      setShimmerActive(true);
      setTimeout(() => setShimmerActive(false), 2000); // Duration of shimmer animation
    }, 18000); // Every 18 seconds

    return () => clearInterval(shimmerInterval);
  }, []);

  useEffect(() => {
    fetchData();
    
    // Expose refresh function for child components
    window.refreshBookingData = async () => {
      await fetchData();
      if (onRefresh) onRefresh();
    };
    
    return () => {
      delete window.refreshBookingData;
    };
  }, []);

  // Update selected day trips when trips state changes and modal is open
  useEffect(() => {
    if (dayModalOpen && selectedDate) {
      const freshBookings = trips.filter(t => t.date === selectedDate).sort((a, b) => a.startTime.localeCompare(b.startTime));
      setSelectedDayBookings(freshBookings);
    }
  }, [trips, selectedDate, dayModalOpen]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [tripsRes, teamMembersRes, assetsRes] = await Promise.all([
        api.get(`/bookings`),
        api.get(`/team`),
        api.get(`/assets`)
      ]);
      
      let bookingsData = tripsRes.data || [];
      
      // Normalize bookings: convert preferredDate/preferredTime to date/startTime for scheduler compatibility
      bookingsData = bookingsData.map(booking => {
        // If booking uses new field names, add aliases for old field names
        if (booking.preferredDate && !booking.date) {
          booking.date = booking.preferredDate;
        }
        if (booking.preferredTime && !booking.startTime) {
          booking.startTime = booking.preferredTime;
        }
        return booking;
      });
      
      console.log('📅 Calendar: Fetched bookings:', bookingsData.length);
      console.log('📅 Calendar: Sample booking dates:', bookingsData.slice(0, 3).map(b => ({id: b.id, date: b.date, startTime: b.startTime})));
      
      setBookings(bookingsData);
      setTeamMembers(teamMembersRes.data || []);
      setAssets(assetsRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load schedule data');
    } finally {
      setLoading(false);
    }
  };

  // Helper to get month start date
  const getMonthStart = () => {
    const start = new Date(currentMonth.getFullYear(), currentMonth.getMonth(), 1);
    return start.toISOString().split('T')[0];
  };

  // Helper to get month end date
  const getMonthEnd = () => {
    const end = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0);
    return end.toISOString().split('T')[0];
  };

  // Helper function to detect orphaned references
  const detectOrphanedReferences = (trip) => {
    const issues = [];
    
    // Check if teamMember exists
    if (trip.teamMemberId && trip.teamMemberId !== 'unassigned') {
      const teamMemberExists = teamMembers.some(c => c.id === trip.teamMemberId);
      if (!teamMemberExists) {
        issues.push('teamMember');
      }
    }
    
    // Check if asset exists
    if (trip.assetId && trip.assetId !== 'unassigned') {
      const assetExists = assets.some(b => b.id === trip.assetId);
      if (!assetExists) {
        issues.push('asset');
      }
    }
    
    return issues;
  };

  // Get trip status - accounts for orphaned references and team assignments
  const getBookingStatus = (trip) => {
    const orphanedIssues = detectOrphanedReferences(trip);
    
    // If teamMember or asset is orphaned (deleted), treat as missing
    const hasTeamMember = trip.teamMemberId && trip.teamMemberId !== 'unassigned' && !orphanedIssues.includes('teamMember');
    const hasAsset = trip.assetId && trip.assetId !== 'unassigned' && !orphanedIssues.includes('asset');
    
    // Check team assignments
    const assignedTeam = (trip.teamAssignments || []).filter(a => a.teamId && a.teamId !== '');
    const hasTeam = assignedTeam.length > 0;
    
    if (hasAsset && hasTeam) {
      // Check if all team slots are filled
      const allFilled = (trip.teamAssignments || []).every(a => a.teamId && a.teamId !== '');
      return allFilled ? 'assigned' : 'partial';
    } else if (hasAsset || hasTeam) {
      return 'partial';
    } else {
      return 'unassigned';
    }
  };

  // Get trip status color - simple colors based on accurate status
  const getBookingStatusColor = (trip) => {
    const status = getBookingStatus(trip);
    
    if (status === 'assigned') {
      return {
        bg: 'bg-green-100',
        border: 'border-green-400',
        text: 'text-green-800',
        badge: 'bg-green-500',
        label: 'Assigned',
        color: '#22c55e'
      };
    } else if (status === 'partial') {
      return {
        bg: 'bg-amber-100',
        border: 'border-amber-400',
        text: 'text-amber-800',
        badge: 'bg-amber-500',
        label: 'Partial',
        color: '#f59e0b'
      };
    } else {
      return {
        bg: 'bg-red-100',
        border: 'border-red-300',
        text: 'text-gray-700',
        badge: 'bg-red-400',
        label: 'Unassigned',
        color: '#ef4444'
      };
    }
  };

  // Date range preset functions
  const applyDateRangePreset = (preset) => {
    const today = new Date();
    let start, end;
    
    switch (preset) {
      case 'today':
        start = end = today.toISOString().split('T')[0];
        break;
      case 'next7':
        start = today.toISOString().split('T')[0];
        end = new Date(today.getTime() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'next30':
        start = today.toISOString().split('T')[0];
        end = new Date(today.getTime() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0];
        break;
      case 'custom':
        setShowCustomDatePicker(true);
        return;
      default:
        return;
    }
    
    setDateRangePreset(preset);
    setStartDate(start);
    setEndDate(end);
    setShowCustomDatePicker(false);
  };

  const handleCustomDateApply = () => {
    if (startDate && endDate) {
      setDateRangePreset('custom');
      setShowCustomDatePicker(false);
    } else {
      toast.error('Please select both start and end dates');
    }
  };

  const getFilteredBookings = () => {
    let filteredBookings = trips;
    
    // Filter by teamMember if "See My Bookings" is enabled
    if (showMyBookingsToggle && showMyBookingsOnly && teamMemberId) {
      filteredBookings = filteredBookings.filter(trip => {
        // Check if team member is in teamAssignments array
        const isAssigned = trip.teamAssignments?.some(assignment => assignment.teamId === teamMemberId);
        return isAssigned;
      });
    }
    
    // Filter by status
    if (statusFilter !== 'all') {
      filteredBookings = filteredBookings.filter(trip => {
        const status = getBookingStatus(trip);
        return status === statusFilter;
      });
    }
    
    // Don't apply date range filtering for calendar view
    if (viewMode === 'calendar') {
      return filteredBookings;
    }
    
    // Filter by date range for list/card views
    if (!startDate || !endDate) {
      return filteredBookings;
    }
    
    return filteredBookings.filter(trip => {
      return trip.date >= startDate && trip.date <= endDate;
    });
  };

  // Calendar helpers
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();
    
    return { daysInMonth, startingDayOfWeek, year, month };
  };

  const getBookingsForDate = (dateStr) => {
    let dateBookings = trips.filter(t => t.date === dateStr);
    
    console.log(`📅 Calendar: Getting bookings for ${dateStr}:`, dateBookings.length, 'found');
    if (dateBookings.length === 0 && trips.length > 0) {
      console.log('📅 Calendar: No bookings for this date. All booking dates:', trips.map(t => t.date));
    }
    
    // Apply "See My Bookings Only" filter if enabled
    if (showMyBookingsToggle && showMyBookingsOnly && teamMemberId) {
      dateBookings = dateBookings.filter(trip => {
        // Check if team member is in teamAssignments array
        const isAssigned = trip.teamAssignments?.some(assignment => assignment.teamId === teamMemberId);
        return isAssigned;
      });
    }
    
    // Apply status filter
    if (statusFilter !== 'all') {
      dateBookings = dateBookings.filter(trip => {
        const status = getBookingStatus(trip);
        return status === statusFilter;
      });
    }
    
    return dateBookings;
  };

  const getDateStatus = (dateStr) => {
    const dayBookings = getBookingsForDate(dateStr);
    if (dayBookings.length === 0) return 'none';
    
    // Use getBookingStatus to check actual status (accounts for orphaned references)
    const allAssigned = dayBookings.every(t => getBookingStatus(t) === 'assigned');
    const allUnassigned = dayBookings.every(t => getBookingStatus(t) === 'unassigned');
    
    if (allAssigned) return 'assigned';
    if (allUnassigned) return 'unassigned';
    return 'partial';
  };

  const handleEditClick = async (trip) => {
    // Mark booking as viewed if it's new
    if (trip.isNew !== false) {
      try {
        await api.post(`/bookings/${trip.id}/mark-viewed`);
        // Update local state
        setBookings(prevBookings => 
          prevBookings.map(b => b.id === trip.id ? {...b, isNew: false} : b)
        );
      } catch (error) {
        console.log('Failed to mark as viewed:', error);
      }
    }
    
    // Open single trip edit modal
    setEditingBooking(trip);
    setEditTeamMemberId(trip.teamMemberId || 'unassigned');
    setEditAssetId(trip.assetId || 'unassigned');
    setEditModalOpen(true);
  };

  // Helper: Check if teamMember is available for a trip (for dropdown graying)
  const isTeamMemberAvailableForBooking = (teamMember, trip) => {
    // Check 1: Unavailable dates
    if (teamMember.unavailableDates && teamMember.unavailableDates.includes(trip.date)) {
      return false;
    }
    
    // Check 2: Time conflicts
    const tripStart = timeToMinutes(trip.startTime);
    const tripEnd = tripStart + trip.duration;
    
    const sameDayBookings = trips.filter(t => 
      t.date === trip.date && 
      t.id !== trip.id && 
      t.teamMemberId === teamMember.id
    );
    
    for (const existingBooking of sameDayBookings) {
      const existingStart = timeToMinutes(existingBooking.startTime);
      const existingEnd = existingStart + existingBooking.duration;
      
      if (tripStart < existingEnd && tripEnd > existingStart) {
        return false; // Time conflict
      }
    }
    
    return true;
  };

  // Helper: Check if asset is available for a trip (for dropdown graying)
  const isAssetAvailableForBooking = (asset, trip) => {
    // Check time conflicts
    const tripStart = timeToMinutes(trip.startTime);
    const tripEnd = tripStart + trip.duration;
    
    const sameDayBookings = trips.filter(t => 
      t.date === trip.date && 
      t.id !== trip.id && 
      t.assetId === asset.id
    );
    
    for (const existingBooking of sameDayBookings) {
      const existingStart = timeToMinutes(existingBooking.startTime);
      const existingEnd = existingStart + existingBooking.duration;
      
      if (tripStart < existingEnd && tripEnd > existingStart) {
        return false; // Time conflict
      }
    }
    
    return true;
  };

  // Helper: Check if teamMember is allowed on asset
  const isTeamMemberAllowedOnAsset = (teamMemberId, assetId) => {
    if (!assetId || assetId === 'unassigned' || !teamMemberId || teamMemberId === 'unassigned') {
      return true;
    }
    
    const asset = assets.find(b => b.id === assetId);
    if (!asset) return true;
    
    // If asset has no allowedTeamMembers or empty array, all teamMembers allowed
    if (!asset.allowedTeamMembers || asset.allowedTeamMembers.length === 0) {
      return true;
    }
    
    // Check if teamMember is in allowed list
    return asset.allowedTeamMembers.includes(teamMemberId);
  };

  // Helper: Check if asset allows selected teamMember
  const isAssetAllowedForTeamMember = (assetId, teamMemberId) => {
    if (!assetId || assetId === 'unassigned' || !teamMemberId || teamMemberId === 'unassigned') {
      return true;
    }
    
    const asset = assets.find(b => b.id === assetId);
    if (!asset) return true;
    
    // If asset has no allowedTeamMembers or empty array, all teamMembers allowed
    if (!asset.allowedTeamMembers || asset.allowedTeamMembers.length === 0) {
      return true;
    }
    
    // Check if teamMember is in allowed list
    return asset.allowedTeamMembers.includes(teamMemberId);
  };

  // Helper: Check time overlap between two trips
  const checkTimeOverlap = (trip1, trip2) => {
    const start1 = timeToMinutes(trip1.startTime);
    const end1 = start1 + trip1.duration;
    const start2 = timeToMinutes(trip2.startTime);
    const end2 = start2 + trip2.duration;
    
    return start1 < end2 && end1 > start2;
  };

  // Helper: Calculate end time from start time and duration
  const calculateEndTime = (startTime, duration) => {
    const startMinutes = timeToMinutes(startTime);
    const endMinutes = startMinutes + duration;
    const hours = Math.floor(endMinutes / 60) % 24;
    const mins = endMinutes % 60;
    return `${String(hours).padStart(2, '0')}:${String(mins).padStart(2, '0')}`;
  };

  // Conflict detection helper functions
  const checkTeamMemberConflicts = (teamMemberId, trip) => {
    if (!teamMemberId || teamMemberId === 'unassigned') return null;
    
    const teamMember = teamMembers.find(c => c.id === teamMemberId);
    if (!teamMember) return null;
    
    const conflicts = [];
    
    // Check 1: Unavailable dates
    if (teamMember.unavailableDates && teamMember.unavailableDates.includes(trip.date)) {
      conflicts.push({
        type: 'unavailable_date',
        message: `${teamMember.name} has marked ${formatDate(trip.date, dateFormat)} as unavailable`
      });
    }
    
    // Check 2: Time conflicts with other trips
    const tripStart = timeToMinutes(trip.startTime);
    const tripEnd = tripStart + trip.duration;
    
    const sameDayBookings = trips.filter(t => 
      t.date === trip.date && 
      t.id !== trip.id && 
      t.teamMemberId === teamMemberId
    );
    
    for (const existingBooking of sameDayBookings) {
      const existingStart = timeToMinutes(existingBooking.startTime);
      const existingEnd = existingStart + existingBooking.duration;
      
      // Check for overlap (allowing exact adjacent times)
      if (tripStart < existingEnd && tripEnd > existingStart) {
        conflicts.push({
          type: 'time_conflict',
          message: `${teamMember.name} is already assigned to a trip at ${formatTime(existingBooking.startTime, timeFormat)} (${existingBooking.duration} min)`
        });
      }
    }
    
    // Check 3: Double-decker certification
    if (editAssetId && editAssetId !== 'unassigned') {
      const asset = assets.find(b => b.id === editAssetId);
      if (asset && asset.type === 'double-decker' && !teamMember.doubleDeckerCertified) {
        conflicts.push({
          type: 'certification',
          message: `${teamMember.name} is not certified for double-decker assets. Asset: ${asset.name}`
        });
      }
    }
    
    return conflicts.length > 0 ? conflicts : null;
  };

  const checkAssetConflicts = (assetId, trip) => {
    if (!assetId || assetId === 'unassigned') return null;
    
    const asset = assets.find(b => b.id === assetId);
    if (!asset) return null;
    
    const conflicts = [];
    
    // Check time conflicts with other trips
    const tripStart = timeToMinutes(trip.startTime);
    const tripEnd = tripStart + trip.duration;
    
    const sameDayBookings = trips.filter(t => 
      t.date === trip.date && 
      t.id !== trip.id && 
      t.assetId === assetId
    );
    
    for (const existingBooking of sameDayBookings) {
      const existingStart = timeToMinutes(existingBooking.startTime);
      const existingEnd = existingStart + existingBooking.duration;
      
      // Check for overlap (allowing exact adjacent times)
      if (tripStart < existingEnd && tripEnd > existingStart) {
        conflicts.push({
          type: 'time_conflict',
          message: `${asset.name} is already assigned to a trip at ${formatTime(existingBooking.startTime, timeFormat)} (${existingBooking.duration} min)`
        });
      }
    }
    
    return conflicts.length > 0 ? conflicts : null;
  };

  const timeToMinutes = (timeStr) => {
    if (!timeStr) return 0;
    const [hours, minutes] = timeStr.split(':').map(Number);
    return hours * 60 + minutes;
  };

  // Detect all conflicts across all trips
  const detectAllConflicts = () => {
    const teamMemberConflicts = [];
    const assetConflicts = [];
    
    // Check each trip for conflicts
    trips.forEach(trip => {
      if (trip.teamMemberId && trip.teamMemberId !== 'unassigned') {
        const conflicts = checkTeamMemberConflicts(trip.teamMemberId, trip);
        if (conflicts && conflicts.length > 0) {
          teamMemberConflicts.push(...conflicts.map(c => ({ ...c, tripId: trip.id, date: trip.date })));
        }
      }
      
      if (trip.assetId && trip.assetId !== 'unassigned') {
        const conflicts = checkAssetConflicts(trip.assetId, trip);
        if (conflicts && conflicts.length > 0) {
          assetConflicts.push(...conflicts.map(c => ({ ...c, tripId: trip.id, date: trip.date })));
        }
      }
    });
    
    return { teamMemberConflicts, assetConflicts };
  };

  const handleSaveEdit = async () => {
    try {
      // Check for conflicts before saving
      const teamMemberConflicts = checkTeamMemberConflicts(editTeamMemberId, editingBooking);
      const assetConflicts = checkAssetConflicts(editAssetId, editingBooking);
      
      const allConflicts = [];
      if (teamMemberConflicts) allConflicts.push(...teamMemberConflicts);
      if (assetConflicts) allConflicts.push(...assetConflicts);
      
      // If conflicts exist, show warning and ask for confirmation
      if (allConflicts.length > 0) {
        const conflictMessages = allConflicts.map(c => `⚠️ ${c.message}`).join('\n\n');
        const confirmMessage = `${conflictMessages}\n\nAre you sure you want to proceed with this assignment?`;
        
        if (!window.confirm(confirmMessage)) {
          return; // User canceled
        }
      }
      
      await api.put(`/bookings/${editingBooking.id}`, {
        date: editingBooking.date,
        startTime: editingBooking.startTime,
        duration: editingBooking.duration,
        teamMemberId: editTeamMemberId === 'unassigned' ? null : editTeamMemberId,
        assetId: editAssetId === 'unassigned' ? null : editAssetId,
        notes: editingBooking.notes || ''
      });
      
      await fetchData();
      if (onRefresh) onRefresh();
      setEditModalOpen(false);
      toast.success('Assignment updated!');
    } catch (error) {
      console.error('Error updating trip:', error);
      toast.error(error.response?.data?.detail || 'Failed to update trip');
    }
  };

  const handleDayClick = async (dateStr) => {
    // Don't open modal in readOnly mode
    if (readOnly) return;
    
    setSelectedDate(dateStr);
    
    // Get trips for this day
    const dayBookings = trips.filter(t => t.date === dateStr);
    setSelectedDayBookings(dayBookings);
    
    // Reset teamMember filter when opening new day
    setSelectedTeamMemberFilters([]);
    
    // Fetch availability for each trip on this day
    const availabilityPromises = dayBookings.map(trip => 
      api.get(`/bookings/${trip.id}/availability`)
        .then(res => ({ tripId: trip.id, data: res.data }))
        .catch(err => {
          console.error(`Error fetching availability for trip ${trip.id}:`, err);
          return { tripId: trip.id, data: null };
        })
    );
    
    const availabilityResults = await Promise.all(availabilityPromises);
    const availabilityMap = {};
    availabilityResults.forEach(result => {
      if (result.data) {
        availabilityMap[result.tripId] = result.data;
      }
    });
    setBookingAvailability(availabilityMap);
    
    setDayModalOpen(true);
  };

  const handlePrevMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1));
  };

  const handleNextMonth = () => {
    setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1));
  };

  // Get trip status based on assignments
  // Get status badge
  const getStatusBadge = (trip) => {
    const status = getBookingStatus(trip);
    
    if (status === 'assigned') {
      return <Badge className="bg-green-100 text-green-800 text-xs">Assigned</Badge>;
    } else if (status === 'partial') {
      return <Badge className="bg-amber-100 text-amber-800 text-xs">Partial</Badge>;
    } else {
      return <Badge className="bg-red-100 text-red-800 text-xs">Unassigned</Badge>;
    }
  };

  // Handle Add Booking
  const handleAddBooking = async (tripData = null) => {
    const data = tripData || newBooking;
    
    if (!data.date || !data.startTime) {
      toast.error('Please fill in date and start time');
      return;
    }

    try {
      const finalData = {
        date: data.date,
        startTime: data.startTime,
        duration: data.duration || 120,
        groupSize: data.groupSize ? parseInt(data.groupSize) : undefined,
        assetId: data.assetId || null,
        notes: data.notes || '',
        teamAssignments: data.teamAssignments || [],
        customerName: data.customerName || undefined,
        customerEmail: data.customerEmail || undefined,
        customerPhone: data.customerPhone || undefined,
        pickupLocation: data.pickupLocation || undefined,
        paymentStatus: data.paymentStatus || 'unpaid',
        totalAmount: data.totalAmount || 0,
        timezone: data.timezone || localStorage.getItem('orgTimezone') || 'America/Chicago'
      };

      let createdBooking;

      // CRITICAL FIX: If manual card payment, process payment FIRST, then create booking
      if (data.paymentStatus === 'manual_card' && data.cardNumber) {
        console.log('=== PROCESSING MANUAL CARD PAYMENT BEFORE BOOKING ===');
        console.log('Amount:', data.totalAmount);
        console.log('Card data present:', {
          cardNumber: !!data.cardNumber,
          cardExpiry: !!data.cardExpiry,
          cardCvc: !!data.cardCvc,
          cardName: !!data.cardName
        });
        
        try {
          // Create Stripe PaymentMethod on frontend (secure way)
          if (!window.Stripe) {
            throw new Error('Stripe.js not loaded. Please refresh the page.');
          }
          
          const stripe = window.Stripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);
          console.log('Stripe instance created');
          
          const [exp_month, exp_year] = data.cardExpiry.split('/');
          console.log('Card expiry parsed:', { exp_month, exp_year });
          
          const { paymentMethod, error } = await stripe.createPaymentMethod({
            type: 'card',
            card: {
              number: data.cardNumber.replace(/\s/g, ''),
              exp_month: parseInt(exp_month),
              exp_year: parseInt(`20${exp_year}`),
              cvc: data.cardCvc,
            },
            billing_details: {
              name: data.cardName,
            }
          });
          
          if (error) {
            console.error('Stripe createPaymentMethod error:', error);
            throw new Error(`Card validation failed: ${error.message}`);
          }
          
          console.log('PaymentMethod created:', paymentMethod.id);
          
          // Now create booking with payment info in backend atomically
          const bookingWithPaymentData = {
            ...finalData,
            paymentMethodId: paymentMethod.id,
            processPayment: true
          };
          
          console.log('=== CREATING BOOKING WITH PAYMENT ===');
          const response = await api.post(`/bookings`, bookingWithPaymentData);
          createdBooking = response.data;
          
          console.log('Booking created with successful payment:', createdBooking);
        } catch (paymentError) {
          console.error('Payment processing failed:', paymentError);
          console.error('Payment error message:', paymentError.message);
          throw new Error(`Payment failed: ${paymentError.message || paymentError.response?.data?.detail || 'Unknown error'}`);
        }
      } else {
        // No payment or other payment methods - create booking normally
        console.log('=== BOOKING CREATION START ===');
        console.log('Final data being sent:', finalData);
        
        const response = await api.post(`/bookings`, finalData);
        createdBooking = response.data;
        
        console.log('=== BOOKING CREATED ===');
        console.log('Created booking:', createdBooking);
        console.log('Booking ID:', createdBooking.id);
        console.log('Confirmation Code:', createdBooking.confirmationCode);
      }
      
      // Send confirmation email if customer email provided
      if (data.customerEmail && data.customerEmail.trim()) {
        console.log('=== SENDING CONFIRMATION EMAIL ===');
        try {
          const emailResponse = await api.post('/bookings/send-confirmation-email', {
            bookingId: createdBooking.id
          });
          console.log('Email sent successfully:', emailResponse.data);
        } catch (emailError) {
          console.error('Failed to send email:', emailError);
          console.error('Email error details:', emailError.response?.data);
        }
      } else {
        console.log('No customer email provided - skipping confirmation email');
      }
      
      console.log('=== RETURNING BOOKING DATA ===');
      
      // Don't close modal or refresh data here - let success modal handle it
      // setAddBookingModalOpen(false); // REMOVED
      // await fetchData(); // MOVED - will be called after success modal closes
      // if (onRefresh) onRefresh(); // MOVED
      
      // Reset form data
      setNewBooking({
        date: '',
        startTime: '',
        endTime: '',
        duration: '',
        groupSize: '',
        teamMemberId: '',
        assetId: '',
        notes: '',
        teamAssignments: []
      });
      
      // Return the created booking for success modal
      return createdBooking;
    } catch (error) {
      console.error('Error adding trip:', error);
      toast.error('Failed to add trip');
      throw error;
    }
  };

  // Calculate duration helper
  const calculateDuration = (start, end) => {
    const [startHour, startMin] = start.split(':').map(Number);
    const [endHour, endMin] = end.split(':').map(Number);
    const totalMinutes = (endHour * 60 + endMin) - (startHour * 60 + startMin);
    return totalMinutes;  // Return integer minutes for backend
  };
  
  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return mins > 0 ? `${hours}h ${mins}m` : `${hours}h`;
  };

  const handleToday = () => {
    setCurrentMonth(new Date());
  };

  const handleHealthCheck = async () => {
    setHealthLoading(true);
    setHealthModalOpen(true);
    
    try {
      const response = await api.get('/health/conflicts', {
        params: { date: format(new Date(), 'yyyy-MM-dd') }
      });
      setHealthData(response.data);
    } catch (error) {
      console.error('Health check error:', error);
      toast.error('Failed to check schedule health');
      setHealthData({ error: 'Failed to load health data' });
    } finally {
      setHealthLoading(false);
    }
  };

  const handleAutoScheduleMonth = async () => {
    try {
      let autoStartDate, autoEndDate;
      
      if (viewMode === 'calendar') {
        const year = currentMonth.getFullYear();
        const month = currentMonth.getMonth();
        autoStartDate = new Date(year, month, 1).toISOString().split('T')[0];
        autoEndDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
      } else {
        // For list/card view, use visible trips to determine date range
        if (trips.length > 0) {
          const dates = trips.map(t => t.date).sort();
          autoStartDate = dates[0];
          autoEndDate = dates[dates.length - 1];
        } else {
          // Default to current month if no trips
          const year = new Date().getFullYear();
          const month = new Date().getMonth();
          autoStartDate = new Date(year, month, 1).toISOString().split('T')[0];
          autoEndDate = new Date(year, month + 1, 0).toISOString().split('T')[0];
        }
      }

      // Get unassigned trips in the date range
      const unassignedBookings = trips.filter(trip => {
        const tripDate = trip.date;
        return tripDate >= autoStartDate && tripDate <= autoEndDate && (!trip.teamMemberId || !trip.assetId);
      });

      // Filter active teamMembers and assets (handle undefined active property)
      const activeTeamMembers = teamMembers.filter(c => c.active !== false);
      const activeAssets = assets.filter(b => b.active !== false);

      // Show confirmation modal with preview
      setSchedulerPreview({
        startDate: autoStartDate,
        endDate: autoEndDate,
        trips: unassignedBookings,
        teamMembers: activeTeamMembers,
        assets: activeAssets
      });
      setShowSchedulerConfirm(true);

    } catch (error) {
      console.error('Error preparing auto-schedule:', error);
      console.error('Error details:', error.stack);
      toast.error(`Failed to prepare auto-schedule: ${error.message}`);
    }
  };

  const executeAutoSchedule = async () => {
    if (!schedulerPreview) return;

    setIsScheduling(true);
    
    try {
      const { startDate, endDate } = schedulerPreview;
      
      const response = await api.post(`/schedule/auto`, { 
        startDate, 
        endDate 
      });
      
      const result = response.data;
      
      // Handle action consumption feedback
      if (result.actions_consumed !== undefined) {
        const { showActionToast } = await import('../utils/actions');
        showActionToast(
          result.actions_consumed, 
          result.actions_remaining, 
          `Auto-scheduled ${result.assignmentsMade} trip${result.assignmentsMade !== 1 ? 's' : ''}`
        );
      }
      
      // Success! Fire confetti! 🎉
      
      await fetchData();
      if (onRefresh) onRefresh();
      
      // Store result and show warnings dialog
      setSchedulerResult(result);
      setWarningsDialogOpen(true);
      
      // Close confirmation modal
      setShowSchedulerConfirm(false);
      setIsScheduling(false);
      
    } catch (error) {
      console.error('Error auto-scheduling:', error);
      
      // Handle insufficient actions error (402)
      if (error.response?.status === 402) {
        const { showInsufficientActionsError } = await import('../utils/actions');
        const detail = error.response.data.detail;
        showInsufficientActionsError(detail.required, detail.balance);
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Auto-schedule failed');
      }
      
      setIsScheduling(false);
    }
  };

  const handleAutoScheduleDay = async () => {
    if (!selectedDate) return;
    
    try {
      // CRITICAL: Clear all pending debounced updates to prevent race conditions
      // If user just manually assigned asset, we need that to save FIRST
      Object.keys(updateTimeouts).forEach(tripId => {
        if (updateTimeouts[tripId]) {
          clearTimeout(updateTimeouts[tripId]);
        }
      });
      setUpdateTimeouts({});
      
      // Wait a moment for any in-flight API calls to complete
      await new Promise(resolve => setTimeout(resolve, 500));
      
      const response = await api.post(`/schedule/auto`, {
        startDate: selectedDate,
        endDate: selectedDate
      });
      
      const result = response.data;
      
      // Handle action consumption feedback
      if (result.actions_consumed !== undefined) {
        const { showActionToast } = await import('../utils/actions');
        showActionToast(
          result.actions_consumed, 
          result.actions_remaining, 
          `Auto-scheduled ${formatDate(selectedDate, dateFormat)}: ${result.assignmentsMade} trip${result.assignmentsMade !== 1 ? 's' : ''}`
        );
      }
      
      // Fetch updated data
      await fetchData();
      if (onRefresh) await onRefresh();
      
      // FORCE REFRESH: Fetch trips directly and update modal state
      // Creating NEW objects to force React re-render
      try {
        const freshBookingsRes = await api.get(`/bookings`);
        const allFreshBookings = freshBookingsRes.data || [];
        
        // Filter for selected date and create NEW objects (force React to detect change)
        const updatedDayBookings = allFreshBookings
          .filter(t => t.date === selectedDate)
          .map(t => ({...t})); // Spread to create new object references
        
        console.log('🔄 Auto-schedule complete - FORCE refreshing modal with', updatedDayBookings.length, 'trips');
        console.log('📊 Updated trips team data:', updatedDayBookings.map(t => ({
          id: t.id.substring(0, 8),
          team: t.teamAssignments?.length || 0,
          status: t.status
        })));
        
        // Force update both states with new object references
        setBookings(allFreshBookings.map(t => ({...t})));
        setSelectedDayBookings(updatedDayBookings);
        
      } catch (refreshError) {
        console.error('Error force-refreshing trips:', refreshError);
      }
      
      // Store result and show warnings dialog
      setSchedulerResult(result);
      setWarningsDialogOpen(true);
      
    } catch (error) {
      console.error('Error auto-scheduling day:', error);
      
      // Handle insufficient actions error (402)
      if (error.response?.status === 402) {
        const { showInsufficientActionsError } = await import('../utils/actions');
        const detail = error.response.data.detail;
        showInsufficientActionsError(detail.required, detail.balance);
      } else {
        toast.error(error.response?.data?.detail?.message || error.response?.data?.detail || 'Auto-schedule failed');
      }
    }
  };

  const handleAssignmentChange = async (tripId, field, value) => {
    try {
      const trip = selectedDayBookings.find(t => t.id === tripId);
      if (!trip) {
        console.error('Booking not found:', tripId);
        toast.error('Booking not found');
        return;
      }

      const updatedBooking = {
        date: trip.date,
        startTime: trip.startTime,
        duration: trip.duration,
        teamMemberId: field === 'teamMember' ? (value === 'unassigned' ? null : value) : (trip.teamMemberId || null),
        assetId: field === 'asset' ? (value === 'unassigned' ? null : value) : (trip.assetId || null),
        notes: trip.notes || ''
      };

      console.log('Updating trip:', tripId, 'with:', updatedBooking);

      await api.put(`/bookings/${tripId}`, updatedBooking);
      
      // Update local state instead of full refresh (keeps modal open)
      const updates = {
        teamMemberId: updatedBooking.teamMemberId,
        assetId: updatedBooking.assetId
      };
      
      setSelectedDayBookings(prevBookings => 
        prevBookings.map(t => t.id === tripId ? { ...t, ...updates } : t)
      );
      
      setBookings(prevBookings => 
        prevBookings.map(t => t.id === tripId ? { ...t, ...updates } : t)
      );
      
      // Don't call fetchData() to prevent modal from closing
    } catch (error) {
      console.error('Error updating assignment:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to update assignment');
    }
  };

  // Generic trip update handler (for team assignments and other fields)
  const handleUpdateBooking = async (tripId, updates) => {
    try {
      const trip = selectedDayBookings.find(t => t.id === tripId);
      if (!trip) {
        console.error('Booking not found:', tripId);
        toast.error('Booking not found');
        return;
      }

      // Update local state immediately for responsiveness
      setSelectedDayBookings(prevBookings => 
        prevBookings.map(t => t.id === tripId ? { ...t, ...updates } : t)
      );
      setBookings(prevBookings => 
        prevBookings.map(t => t.id === tripId ? { ...t, ...updates } : t)
      );

      // Clear existing timeout for this trip
      if (updateTimeouts[tripId]) {
        clearTimeout(updateTimeouts[tripId]);
      }

      // Debounce the API call
      const timeoutId = setTimeout(async () => {
        try {
          const updatedBooking = {
            date: trip.date,
            startTime: trip.startTime,
            duration: trip.duration,
            notes: trip.notes || '',
            ...updates,  // Apply updates FIRST
            // Then fill in missing fields from current trip state
            teamMemberId: updates.teamMemberId !== undefined ? updates.teamMemberId : (trip.teamMemberId || null),
            assetId: updates.assetId !== undefined ? updates.assetId : (trip.assetId || null),
            teamAssignments: updates.teamAssignments !== undefined ? updates.teamAssignments : (trip.teamAssignments || [])
          };

          console.log('Saving trip updates:', tripId, updatedBooking);

          await api.put(`/bookings/${tripId}`, updatedBooking);
          
          toast.success('Saved!', { duration: 1500 });
        } catch (error) {
          console.error('Error saving trip:', error);
          toast.error('Failed to save changes');
          // Revert local state on error
          onRefresh();
        }
      }, 1000); // 1 second debounce

      setUpdateTimeouts(prev => ({ ...prev, [tripId]: timeoutId }));

    } catch (error) {
      console.error('Error updating trip:', error);
      toast.error('Update failed');
    }
  };

  // Mass actions handlers
  const toggleMassActionMode = () => {
    setMassActionMode(!massActionMode);
    if (massActionMode) {
      // Clear selections when turning off mass action mode
      setSelectedBookingIds([]);
    }
  };

  const handleSelectAll = () => {
    const visibleBookings = getSortedBookings();
    if (selectedBookingIds.length === visibleBookings.length) {
      setSelectedBookingIds([]);
    } else {
      setSelectedBookingIds(visibleBookings.map(t => t.id));
    }
  };

  const handleSelectBooking = (tripId) => {
    setSelectedBookingIds(prev => {
      if (prev.includes(tripId)) {
        return prev.filter(id => id !== tripId);
      } else {
        return [...prev, tripId];
      }
    });
  };

  const handleBulkDelete = async () => {
    if (selectedBookingIds.length === 0) {
      toast.error('No trips selected');
      return;
    }

    try {
      await Promise.all(
        selectedBookingIds.map(tripId => 
          api.delete(`/bookings/${tripId}`)
        )
      );

      toast.success(`Successfully deleted ${selectedBookingIds.length} trip(s)`);
      setSelectedBookingIds([]);
      setShowDeleteConfirm(false);
      await fetchData();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting trips:', error);
      toast.error('Failed to delete trips');
    }
  };

  const handleDeleteSingleBooking = async (tripId) => {
    try {
      await api.delete(`/bookings/${tripId}`);
      toast.success('Booking deleted successfully');
      await fetchData();
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error deleting trip:', error);
      toast.error('Failed to delete trip');
    }
  };

  const handleAddBookingFromCalendar = () => {
    setShowAddModal(true);
  };

  const getAvailableTeamMembers = () => {
    if (!selectedDate) return [];
    
    const dayBookings = getBookingsForDate(selectedDate);
    const assignedTeamMemberIds = dayBookings.map(t => t.teamMemberId).filter(Boolean);
    
    return teamMembers.filter(c => !assignedTeamMemberIds.includes(c.id));
  };

  const getWorkingTeamMembers = () => {
    if (!selectedDate) return [];
    
    const dayBookings = getBookingsForDate(selectedDate);
    const assignedTeamMemberIds = [...new Set(dayBookings.map(t => t.teamMemberId).filter(Boolean))];
    
    return teamMembers.filter(c => assignedTeamMemberIds.includes(c.id));
  };

  const getAvailableAssets = () => {
    if (!selectedDate) return [];
    
    const dayBookings = getBookingsForDate(selectedDate);
    const assignedAssetIds = dayBookings.map(t => t.assetId).filter(Boolean);
    
    return assets.filter(b => b.active && !assignedAssetIds.includes(b.id));
  };

  const getInUseAssets = () => {
    if (!selectedDate) return [];
    
    const dayBookings = getBookingsForDate(selectedDate);
    const assignedAssetIds = [...new Set(dayBookings.map(t => t.assetId).filter(Boolean))];
    
    return assets.filter(b => assignedAssetIds.includes(b.id));
  };

  const getTeamMemberName = (teamMemberId) => {
    const teamMember = teamMembers.find(c => c.id === teamMemberId);
    return teamMember ? teamMember.name : 'Unassigned';
  };

  const getAssetName = (assetId) => {
    const asset = assets.find(b => b.id === assetId);
    return asset ? asset.name : 'Unassigned';
  };

  const getAssetType = (assetId) => {
    const asset = assets.find(b => b.id === assetId);
    return asset ? asset.type : '';
  };

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <ArrowUpDown className="w-3 h-3 ml-1 inline" />;
    return sortDirection === 'asc' ? 
      <ArrowUp className="w-3 h-3 ml-1 inline" /> : 
      <ArrowDown className="w-3 h-3 ml-1 inline" />;
  };

  const getSortedBookings = () => {
    const filteredBookings = getFilteredBookings();
    return [...filteredBookings].sort((a, b) => {
      let compareValue = 0;
      
      switch (sortField) {
        case 'date':
          compareValue = a.date.localeCompare(b.date);
          if (compareValue === 0) {
            compareValue = a.startTime.localeCompare(b.startTime);
          }
          break;
        case 'time':
          compareValue = a.startTime.localeCompare(b.startTime);
          break;
        case 'teamMember':
          compareValue = getTeamMemberName(a.teamMemberId).localeCompare(getTeamMemberName(b.teamMemberId));
          break;
        case 'asset':
          compareValue = getAssetName(a.assetId).localeCompare(getAssetName(b.assetId));
          break;
        case 'status':
          compareValue = a.status.localeCompare(b.status);
          break;
        default:
          compareValue = 0;
      }
      
      return sortDirection === 'asc' ? compareValue : -compareValue;
    });
  };

  // Render calendar grid
  const renderCalendar = () => {
    const { daysInMonth, startingDayOfWeek, year, month } = getDaysInMonth(currentMonth);
    const days = [];
    
    // Empty cells for days before month starts
    for (let i = 0; i < startingDayOfWeek; i++) {
      days.push(<div key={`empty-${i}`} className="h-16 sm:h-20 bg-gray-50"></div>);
    }
    
    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const status = getDateStatus(dateStr);
      const tripCount = getBookingsForDate(dateStr).length;
      // Use local date for "today" comparison to avoid timezone issues
      const today = new Date();
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const isToday = dateStr === todayStr;
      
      const statusColors = {
        none: 'bg-white',
        assigned: 'bg-green-50 border-green-200',
        partial: 'bg-amber-50 border-amber-200',
        unassigned: 'bg-red-50 border-red-200'
      };
      
      days.push(
        <div
          key={day}
          onClick={() => handleDayClick(dateStr)}
          className={`h-16 sm:h-20 border p-2 ${!readOnly ? 'cursor-pointer hover:bg-gray-100' : ''} transition-colors ${statusColors[status]} ${
            isToday ? 'ring-2 ring-blue-500' : ''
          }`}
        >
          <div className="flex justify-between items-start mb-1">
            <span className={`text-sm font-medium ${isToday ? 'text-blue-600' : 'text-gray-700'}`}>
              {day}
            </span>
            {tripCount > 0 && (
              <Badge className={`text-xs h-5 ${
                status === 'assigned' ? 'bg-green-500' :
                status === 'partial' ? 'bg-amber-500' :
                'bg-red-500'
              } text-white`}>
                {tripCount}
              </Badge>
            )}
          </div>
          {tripCount > 0 && (
            <div className="text-xs text-gray-600">
              {tripCount} trip{tripCount !== 1 ? 's' : ''}
            </div>
          )}
        </div>
      );
    }
    
    return days;
  };

  if (loading) {
    return <LoadingSpinner text="Loading calendar..." />;
  }

  return (
    <DashboardPageLayout
      title={readOnly ? 'Schedule' : 'Scheduler'}
      subtitle={viewMode === 'calendar' ? currentMonth.toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : undefined}
      guideIcon={GUIDE_CONTENT.schedule.icon}
      guideTitle={GUIDE_CONTENT.schedule.title}
      guideDescription={GUIDE_CONTENT.schedule.description}
      guideActions={GUIDE_CONTENT.schedule.actions}
      onGuideActionClick={(action) => {
        if (action === 'demo' && onDemoClick) {
          onDemoClick();
        }
      }}
      actions={
        <div className="flex items-center gap-2 flex-wrap">
          {/* Calendar Navigation - only in calendar view */}
          {viewMode === 'calendar' && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handlePrevMonth}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleNextMonth}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <Button variant="outline" size="sm" onClick={handleToday}>
                Today
              </Button>
            </div>
          )}
          
          {/* "See My Bookings" Toggle for TeamMember View */}
          {showMyBookingsToggle && (
            <div className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 shadow-sm border">
              <input
                type="checkbox"
                id="myBookingsToggle"
                checked={showMyBookingsOnly}
                onChange={(e) => setShowMyBookingsOnly(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
              />
              <label htmlFor="myBookingsToggle" className="text-sm font-medium text-gray-700 cursor-pointer">
                See My Bookings Only
              </label>
            </div>
          )}
          
          {/* Mass Actions Button - only show in list/card views and not read-only */}
          {!readOnly && (viewMode === 'list' || viewMode === 'card') && (
            <Button
              onClick={toggleMassActionMode}
              variant={massActionMode ? "default" : "outline"}
              size="sm"
              style={massActionMode ? { background: '#007AFF', color: 'white' } : {}}
              className="text-xs sm:text-sm"
            >
              <CheckSquare className="w-4 h-4 mr-1 sm:mr-2" />
              Mass Actions
            </Button>
          )}
          
          <CardListViewToggle 
            viewMode={viewMode} 
            onViewModeChange={setViewMode} 
            showCalendar={true} 
          />
          
          {/* Actions Dropdown - only show in admin mode */}
          {!readOnly && (
            <Popover open={actionsPopoverOpen} onOpenChange={setActionsPopoverOpen}>
              <PopoverTrigger asChild>
                <Button 
                  style={{ background: '#007AFF' }}
                  className="text-white shadow-md hover:opacity-90"
                >
                  <MoreVertical className="w-4 h-4 mr-2" />
                  Actions
                  <ChevronDown className="w-4 h-4 ml-2" />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-56" align="end">
                <div className="space-y-1">
                  {/* Add Booking */}
                  <button
                    onClick={() => {
                      setAddBookingModalOpen(true);
                      setActionsPopoverOpen(false);
                    }}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-green-50 flex items-center gap-3 text-green-600 font-medium transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                    Add Booking
                  </button>
                  
                  {/* Bulk Import */}
                  <button
                    onClick={() => {
                      setBulkImportOpen(true);
                      setActionsPopoverOpen(false);
                    }}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-purple-50 flex items-center gap-3 text-purple-600 font-medium transition-colors"
                  >
                    <Upload className="w-5 h-5" />
                    Bulk Import
                  </button>
                  
                  {/* Auto Schedule */}
                  <button
                    onClick={() => {
                      setActionsPopoverOpen(false);
                      handleAutoScheduleMonth();
                    }}
                    className="w-full text-left px-3 py-2.5 rounded-lg hover:bg-gradient-to-r hover:from-purple-50 hover:to-pink-50 flex items-center gap-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium shadow-sm transition-all"
                  >
                    <Zap className="w-5 h-5" />
                    AI Auto Schedule
                  </button>
                </div>
              </PopoverContent>
            </Popover>
          )}
        </div>
      }
    >
      <div className="space-y-4">
      {/* Status Filter Badges & Timezone - Shown for all views */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm font-medium text-gray-600">Filter:</span>
          <div className="flex gap-2">
            <button
              onClick={() => setStatusFilter(statusFilter === 'all' ? 'all' : 'all')}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                statusFilter === 'all' 
                  ? 'bg-gray-200 text-gray-800 shadow-sm' 
                  : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <span className="w-2 h-2 rounded-full bg-gray-400"></span>
              <span className="hidden sm:inline">All Bookings</span>
              <span className="sm:hidden">All</span>
            </button>
          
          <button
            onClick={() => setStatusFilter(statusFilter === 'assigned' ? 'all' : 'assigned')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              statusFilter === 'assigned' 
                ? 'bg-green-100 text-green-800 shadow-sm' 
                : 'bg-white text-gray-600 hover:bg-green-50 border border-gray-200'
            }`}
          >
            <div className="w-2 h-2 rounded-full bg-green-500"></div>
            <span className="hidden sm:inline">Fully Assigned</span>
            <span className="sm:hidden">Ready</span>
          </button>
          
          <button
            onClick={() => setStatusFilter(statusFilter === 'partial' ? 'all' : 'partial')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              statusFilter === 'partial' 
                ? 'bg-amber-100 text-amber-800 shadow-sm' 
                : 'bg-white text-gray-600 hover:bg-amber-50 border border-gray-200'
            }`}
          >
            <div className="w-2 h-2 rounded-full bg-amber-500"></div>
            <span className="hidden sm:inline">Partially Assigned</span>
            <span className="sm:hidden">Partial</span>
          </button>
          
          <button
            onClick={() => setStatusFilter(statusFilter === 'unassigned' ? 'all' : 'unassigned')}
            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
              statusFilter === 'unassigned' 
                ? 'bg-red-100 text-red-800 shadow-sm' 
                : 'bg-white text-gray-600 hover:bg-red-50 border border-gray-200'
            }`}
          >
            <div className="w-2 h-2 rounded-full bg-red-500"></div>
            <span className="hidden sm:inline">Unassigned</span>
            <span className="sm:hidden">Need</span>
          </button>
        </div>
        </div>
        
        <div className="flex items-center gap-2">
        {/* Conflict Indicator */}
        {!readOnly && (() => {
          const conflicts = detectAllConflicts();
          const hasConflicts = conflicts.teamMemberConflicts.length > 0 || conflicts.assetConflicts.length > 0;
          const totalConflicts = conflicts.teamMemberConflicts.length + conflicts.assetConflicts.length;
          
          return (
            <div 
              className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm font-medium cursor-pointer transition-all ${
                hasConflicts 
                  ? 'bg-orange-100 text-orange-800 hover:bg-orange-200' 
                  : 'text-green-600 hover:text-green-700'
              }`}
              onClick={() => hasConflicts && handleHealthCheck()}
              title={hasConflicts ? 'Click to view conflicts' : 'No scheduling conflicts'}
            >
              {hasConflicts ? (
                <>
                  <AlertCircle className="w-4 h-4" />
                  <span>{totalConflicts} conflict{totalConflicts !== 1 ? 's' : ''}</span>
                </>
              ) : (
                <>
                  <Check className="w-4 h-4" />
                  <span>No conflicts</span>
                </>
              )}
            </div>
          );
        })()}
        </div>
      </div>

      {/* Date Range Controls for List/Card views */}
      {(viewMode === 'list' || viewMode === 'card') && (
        <div className="bg-white rounded-lg p-4 shadow-sm border space-y-3">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-medium text-gray-700">Date Range:</span>
            <div className="flex gap-2">
              <Button
                variant={dateRangePreset === 'today' ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyDateRangePreset('today')}
                style={dateRangePreset === 'today' ? { background: '#007AFF', color: 'white' } : {}}
              >
                Today
              </Button>
              <Button
                variant={dateRangePreset === 'next7' ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyDateRangePreset('next7')}
                style={dateRangePreset === 'next7' ? { background: '#007AFF', color: 'white' } : {}}
              >
                Next 7 Days
              </Button>
              <Button
                variant={dateRangePreset === 'next30' ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyDateRangePreset('next30')}
                style={dateRangePreset === 'next30' ? { background: '#007AFF', color: 'white' } : {}}
              >
                Next 30 Days
              </Button>
              <Button
                variant={dateRangePreset === 'custom' ? 'default' : 'outline'}
                size="sm"
                onClick={() => applyDateRangePreset('custom')}
                style={dateRangePreset === 'custom' ? { background: '#007AFF', color: 'white' } : {}}
              >
                Custom
              </Button>
            </div>
          </div>
          
          {/* Custom Date Picker */}
          {showCustomDatePicker && (
            <div className="flex items-end gap-3 pt-2 border-t">
              <div className="flex-1">
                <Label className="text-xs text-gray-600">Start Date</Label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                />
              </div>
              <div className="flex-1">
                <Label className="text-xs text-gray-600">End Date</Label>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-full px-3 py-2 border rounded-md text-sm mt-1"
                />
              </div>
              <Button
                size="sm"
                onClick={handleCustomDateApply}
                style={{ background: '#007AFF' }}
                className="text-white"
              >
                Apply Range
              </Button>
            </div>
          )}
          
          {/* Display current range */}
          {!showCustomDatePicker && startDate && endDate && (
            <div className="text-sm text-gray-600">
              Showing trips from <span className="font-semibold">{formatDate(startDate, dateFormat)}</span> to <span className="font-semibold">{formatDate(endDate, dateFormat)}</span>
            </div>
          )}
        </div>
      )}

      {/* Calendar Grid */}
      {viewMode === 'calendar' && (
        <>
          <Card>
            <CardContent className="p-4">
              {/* Day headers */}
              <div className="grid grid-cols-7 gap-0 mb-2">
                {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                  <div key={day} className="text-center font-semibold text-gray-600 py-2">
                    {day}
                  </div>
                ))}
              </div>
              {/* Calendar days */}
              <div className="grid grid-cols-7 gap-0 border-t border-l">
                {renderCalendar()}
              </div>
            </CardContent>
          </Card>
          
          {/* Timezone indicator below calendar */}
          <div className="text-xs text-gray-500 text-center mt-2">
            Viewing in {getTimezoneAbbreviation(orgTimezone)}
          </div>
        </>
      )}

      {/* List View */}
      {viewMode === 'list' && (
        <>
          {getSortedBookings().length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">No trips scheduled</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mass Actions Toolbar */}
              {!readOnly && selectedBookingIds.length > 0 && (
                <MassActionToolbar
                  selectedCount={selectedBookingIds.length}
                  onSelectAll={handleSelectAll}
                  onDeselectAll={() => setSelectedBookingIds([])}
                  onDelete={() => setShowDeleteConfirm(true)}
                  itemName="trip"
                />
              )}

              <div className="bg-white rounded-lg shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      {!readOnly && massActionMode && (
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-12">
                          <input
                            type="checkbox"
                            checked={selectedBookingIds.length === getSortedBookings().length && getSortedBookings().length > 0}
                            onChange={handleSelectAll}
                            className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                          />
                        </th>
                      )}
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Confirmation
                      </th>
                      <th 
                        onClick={() => handleSort('date')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      >
                        Date {getSortIcon('date')}
                      </th>
                      <th 
                        onClick={() => handleSort('time')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      >
                        Time {getSortIcon('time')}
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Service
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Asset
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Team
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Customer
                      </th>
                      <th 
                        onClick={() => handleSort('status')}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100 transition-colors"
                      >
                        Status {getSortIcon('status')}
                      </th>
                      {!readOnly && (
                        <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                          Actions
                        </th>
                      )}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {getSortedBookings().map((trip) => {
                      const asset = assets?.find(b => b.id === trip.assetId);
                      const assignedTeam = trip.teamAssignments?.filter(a => a.teamId) || [];
                      
                      return (
                      <tr key={trip.id} className="hover:bg-gray-50">
                        {!readOnly && massActionMode && (
                          <td className="px-4 py-4 whitespace-nowrap">
                            <input
                              type="checkbox"
                              checked={selectedBookingIds.includes(trip.id)}
                              onChange={() => handleSelectBooking(trip.id)}
                              className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                            />
                          </td>
                        )}
                        <td className="px-6 py-4 whitespace-nowrap">
                          {trip.confirmationCode ? (
                            <div>
                              <a 
                                href={`/customer-ticket/${trip.confirmationCode}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="font-mono font-semibold text-sm text-blue-600 hover:text-blue-700 underline"
                                title="View customer ticket"
                              >
                                {trip.confirmationCode}
                              </a>
                              {trip.waiverSigned && trip.waiverOtp && (
                                <p className="font-mono text-xs text-green-600 flex items-center gap-1" title="Waiver PIN">
                                  <Check className="w-3 h-3" />
                                  PIN: {trip.waiverOtp}
                                </p>
                              )}
                              {trip.waiverSignatures && trip.waiverSignatures.length > 0 && !trip.waiverOtp && (
                                <p className="font-mono text-xs text-green-600 flex items-center gap-1" title="Waiver signed">
                                  <Check className="w-3 h-3" />
                                  Signed
                                </p>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 text-sm">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {formatDate(trip.date, dateFormat)}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex items-center gap-2">
                            <Clock className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-semibold text-gray-900">
                              {formatTime(trip.startTime, timeFormat)}
                            </span>
                            {trip.timezone && (
                              <span className="text-xs font-medium text-gray-500">
                                {getTimezoneAbbreviation(trip.timezone)}
                              </span>
                            )}
                            <span className="text-xs text-gray-500">({trip.duration}min)</span>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {trip.serviceName || <span className="text-gray-400 italic">-</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm text-gray-900">
                            {asset?.name || <span className="text-gray-400 italic">Not assigned</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {assignedTeam.length > 0 ? (
                            <div className="space-y-1">
                              {assignedTeam.slice(0, 2).map((team, idx) => {
                                const role = roles.find(r => r.id === team.roleId);
                                const teamMember = teamMembers?.find(c => c.id === team.teamId);
                                return (
                                  <div key={idx} className="text-sm">
                                    <div className="flex items-center gap-1">
                                      <span 
                                        className="w-2 h-2 rounded-full" 
                                        style={{ backgroundColor: role?.color || '#6B7280' }}
                                      />
                                      <span className="font-medium">{team.teamName}</span>
                                    </div>
                                    {teamMember?.phone && (
                                      <a href={`tel:${teamMember.phone}`} className="text-xs text-blue-600 hover:underline ml-3">
                                        {teamMember.phone}
                                      </a>
                                    )}
                                  </div>
                                );
                              })}
                              {assignedTeam.length > 2 && (
                                <span className="text-xs text-gray-500">+{assignedTeam.length - 2} more</span>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400 italic">Not assigned</span>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          {trip.customerName ? (
                            <div className="text-sm">
                              <div className="font-medium text-gray-900">{formatCustomerName(trip.customerName)}</div>
                              {trip.customerPhone && (
                                <a href={`tel:${trip.customerPhone}`} className="text-xs text-blue-600 hover:underline">
                                  {trip.customerPhone}
                                </a>
                              )}
                            </div>
                          ) : (
                            <span className="text-sm text-gray-400">-</span>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {getStatusBadge(trip)}
                        </td>
                        {!readOnly && (
                          <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingBooking(trip);
                                setEditModalOpen(true);
                              }}
                              className="text-blue-600 hover:text-blue-700"
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          </td>
                        )}
                      </tr>
                    );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </>
      )}

      {/* Card View */}
      {viewMode === 'card' && (
        <>
          {getSortedBookings().length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <Calendar className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                <p className="text-gray-500 text-lg">No trips scheduled</p>
              </CardContent>
            </Card>
          ) : (
            <>
              {/* Mass Actions Toolbar */}
              {!readOnly && selectedBookingIds.length > 0 && (
                <MassActionToolbar
                  selectedCount={selectedBookingIds.length}
                  onSelectAll={handleSelectAll}
                  onDeselectAll={() => setSelectedBookingIds([])}
                  onDelete={() => setShowDeleteConfirm(true)}
                  itemName="trip"
                />
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {getSortedBookings().map((trip) => {
                  const statusInfo = getBookingStatusColor(trip);
                  const asset = assets?.find(b => b.id === trip.assetId);
                  const assignedTeam = trip.teamAssignments?.filter(a => a.teamId) || [];
                  
                  return (
                    <Card 
                      key={trip.id} 
                      className="relative bg-white hover:shadow-md transition-shadow overflow-hidden"
                      style={{ borderLeft: `4px solid ${statusInfo.color}` }}
                    >
                      
                      {/* Checkbox for mass actions */}
                      {!readOnly && massActionMode && (
                        <input
                          type="checkbox"
                          checked={selectedBookingIds.includes(trip.id)}
                          onChange={(e) => {
                            e.stopPropagation();
                            handleSelectBooking(trip.id);
                          }}
                          className="absolute top-3 right-3 w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer z-10"
                        />
                      )}
                      
                      <CardContent className="p-4 pl-6">
                        {/* Header: Confirmation + Time/Status */}
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <span className="text-xs text-gray-500 uppercase tracking-wide">Confirmation</span>
                            <p className="font-mono font-bold text-sm text-blue-600">{trip.confirmationCode}</p>
                          </div>
                          <div className="text-right">
                            <div className="flex items-center gap-2 justify-end mb-1">
                              <Clock className="w-4 h-4 text-gray-600" />
                              <span className="font-bold text-lg text-gray-900">
                                {formatTime(trip.startTime, timeFormat)}
                              </span>
                              {trip.timezone && (
                                <span className="text-xs font-medium text-blue-600 px-1.5 py-0.5 bg-blue-50 rounded">
                                  {getTimezoneAbbreviation(trip.timezone)}
                                </span>
                              )}
                            </div>
                            <Badge className={statusInfo.badge}>{statusInfo.label}</Badge>
                          </div>
                        </div>

                        {/* Date + Duration */}
                        <div className="flex items-center gap-3 text-sm text-gray-600 mb-3 pb-3 border-b">
                          <span>{formatDate(trip.date, dateFormat)}</span>
                          <span>•</span>
                          <span>{trip.duration} min</span>
                        </div>
                        
                        {/* Service Name */}
                        {trip.serviceName && (
                          <div className="mb-3">
                            <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
                              <Package className="w-3 h-3" />
                              Service
                            </div>
                            <p className="text-sm font-medium text-gray-900">{trip.serviceName}</p>
                          </div>
                        )}
                        
                        {/* Core Info: Asset + Team Summary */}
                        <div className="grid grid-cols-2 gap-3 mb-3">
                          <div>
                            <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
                              <Package className="w-3 h-3" />
                              Asset
                            </div>
                            <p className="text-sm font-medium text-gray-900">
                              {asset?.name || <span className="text-gray-400 italic">Not assigned</span>}
                            </p>
                          </div>
                          
                          <div>
                            <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
                              <Users className="w-3 h-3" />
                              Team
                            </div>
                            <p className="text-sm font-medium text-gray-900">
                              {assignedTeam.length > 0 ? `${assignedTeam.length} assigned` : <span className="text-gray-400 italic">Not assigned</span>}
                            </p>
                          </div>
                        </div>

                        {/* Expandable Details */}
                        {(assignedTeam.length > 0 || trip.customerName || trip.customerPhone || trip.notes) && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedBookingDetails(prev => ({
                                ...prev,
                                [trip.id]: !prev[trip.id]
                              }));
                            }}
                            className="w-full text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1 py-2 hover:bg-blue-50 rounded transition-colors"
                          >
                            {expandedBookingDetails[trip.id] ? (
                              <>
                                <ChevronDown className="w-3 h-3 rotate-180 transition-transform" />
                                Show Less
                              </>
                            ) : (
                              <>
                                <ChevronDown className="w-3 h-3 transition-transform" />
                                See More
                              </>
                            )}
                          </button>
                        )}

                        {/* Expanded Details */}
                        {expandedBookingDetails[trip.id] && (
                          <div className="mt-3 pt-3 border-t space-y-3">
                            {/* Team Details */}
                            {assignedTeam.length > 0 && (
                              <div>
                                <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-2">
                                  <Users className="w-3 h-3" />
                                  Team Members ({assignedTeam.length})
                                </div>
                                <div className="space-y-1.5">
                                  {assignedTeam.map((team, idx) => {
                                    const role = roles.find(r => r.id === team.roleId);
                                    const teamMember = teamMembers?.find(c => c.id === team.teamId);
                                    return (
                                      <div key={idx} className="bg-gray-50 rounded p-2">
                                        <div className="flex items-center gap-1.5">
                                          <span 
                                            className="w-2 h-2 rounded-full" 
                                            style={{ backgroundColor: role?.color || '#6B7280' }}
                                          />
                                          <span className="text-sm font-medium text-gray-900">
                                            {team.teamName}
                                          </span>
                                          <span className="text-xs text-gray-500">({team.roleName})</span>
                                        </div>
                                        {teamMember?.phone && (
                                          <a href={`tel:${teamMember.phone}`} className="text-xs text-blue-600 hover:underline ml-3.5 flex items-center gap-1 mt-1">
                                            <Phone className="w-3 h-3" />
                                            {teamMember.phone}
                                          </a>
                                        )}
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                            
                            {/* Customer */}
                            {(trip.customerName || trip.customerPhone) && (
                              <div>
                                <div className="flex items-center gap-1 text-xs text-gray-500 uppercase tracking-wide mb-1">
                                  <User className="w-3 h-3" />
                                  Customer
                                </div>
                                {trip.customerName && (
                                  <p className="text-sm text-gray-900 mb-0.5">{formatCustomerName(trip.customerName)}</p>
                                )}
                                {trip.customerPhone && (
                                  <a href={`tel:${trip.customerPhone}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                    <Phone className="w-3 h-3" />
                                    {trip.customerPhone}
                                  </a>
                                )}
                              </div>
                            )}
                            
                            {/* Notes */}
                            {trip.notes && (
                              <div>
                                <p className="text-xs text-gray-500 mb-1">Notes:</p>
                                <p className="text-sm text-gray-700">{trip.notes}</p>
                              </div>
                            )}

                            {/* Copy Link */}
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                const link = `${window.location.origin}/customer-ticket/${trip.confirmationCode}`;
                                navigator.clipboard.writeText(link);
                                toast.success('Customer link copied!');
                              }}
                              className="w-full text-xs text-blue-600 hover:text-blue-700 flex items-center justify-center gap-1 py-2 hover:bg-blue-50 rounded transition-colors"
                            >
                              <Link2 className="w-3 h-3" />
                              Copy Customer Link
                            </button>
                          </div>
                        )}

                        {/* Action Buttons */}
                        {!readOnly && !massActionMode && (
                          <div className="flex gap-2 mt-3 pt-3 border-t">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setEditingBooking(trip);
                                setEditModalOpen(true);
                              }}
                              className="flex-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                            >
                              <Edit className="w-3 h-3 mr-1" />
                              Edit
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => {
                                setBookingToDelete(trip);
                                setShowDeleteConfirm(true);
                              }}
                              className="flex-1 text-red-500 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-3 h-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </>
          )}
        </>
      )}

      {/* Day Detail Modal (for Calendar view) */}
      <Dialog open={dayModalOpen} onOpenChange={setDayModalOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedDate && formatDate(selectedDate, dateFormat)}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* Resource Availability Overview - Collapsible */}
            {selectedDayBookings.length > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <button
                  onClick={() => setShowResourceOverview(!showResourceOverview)}
                  className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 hover:text-gray-900"
                >
                  <span className="flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Resource Availability
                  </span>
                  <ChevronDown className={`w-4 h-4 transition-transform ${showResourceOverview ? 'rotate-180' : ''}`} />
                </button>
                
                {showResourceOverview && (
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
                    {/* Assets Status */}
                    <div className="bg-white border border-gray-200 rounded-lg p-3">
                      <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                        <Package className="w-4 h-4" />
                        Assets Today
                      </h4>
                      <div className="space-y-1.5">
                    {assets.filter(b => b.active).map(asset => {
                      const tripsOnAsset = selectedDayBookings.filter(t => t.assetId === asset.id);
                      const isInUse = tripsOnAsset.length > 0;
                      
                      return (
                        <div key={asset.id} className={`flex items-center justify-between text-sm p-1.5 rounded ${isInUse ? 'bg-green-50' : 'bg-gray-50'}`}>
                          <span className={isInUse ? 'font-medium text-green-900' : 'text-gray-600'}>
                            {asset.name}
                          </span>
                          <span className={`text-xs ${isInUse ? 'text-green-700' : 'text-gray-500'}`}>
                            {isInUse ? `${tripsOnAsset.length} trip${tripsOnAsset.length > 1 ? 's' : ''}` : 'Available'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
                
                {/* Team Status */}
                <div className="bg-white border border-gray-200 rounded-lg p-3">
                  <h4 className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    Team Today
                  </h4>
                  <div className="space-y-1.5">
                    {teamMembers.slice(0, 6).map(teamMember => {
                      const assignedBookings = selectedDayBookings.filter(t => 
                        t.teamAssignments?.some(a => a.teamId === teamMember.id)
                      );
                      const isWorking = assignedBookings.length > 0;
                      const role = roles.find(r => r.id === teamMember.roleId);
                      
                      return (
                        <div key={teamMember.id} className={`flex items-center justify-between text-sm p-1.5 rounded ${isWorking ? 'bg-blue-50' : 'bg-gray-50'}`}>
                          <div className="flex items-center gap-2">
                            <span 
                              className="w-2 h-2 rounded-full" 
                              style={{ backgroundColor: role?.color || '#6B7280' }}
                            />
                            <span className={isWorking ? 'font-medium text-blue-900' : 'text-gray-600'}>
                              {teamMember.name}
                            </span>
                          </div>
                          <span className={`text-xs ${isWorking ? 'text-blue-700' : 'text-gray-500'}`}>
                            {isWorking ? `${assignedBookings.length} trip${assignedBookings.length > 1 ? 's' : ''}` : 'Available'}
                          </span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
                )}
              </div>
            )}
            
            {/* TeamMember Filter Section */}
            {selectedDayBookings.length > 0 && (() => {
              // Get unique teamMembers with trips this day
              const teamMembersWithBookings = {};
              selectedDayBookings.forEach(trip => {
                if (trip.teamMemberId) {
                  const teamMember = teamMembers.find(c => c.id === trip.teamMemberId);
                  if (teamMember) {
                    if (!teamMembersWithBookings[teamMember.id]) {
                      teamMembersWithBookings[teamMember.id] = {
                        teamMember,
                        tripCount: 0,
                        assets: new Set()
                      };
                    }
                    teamMembersWithBookings[teamMember.id].tripCount++;
                    if (trip.assetId) {
                      const asset = assets.find(b => b.id === trip.assetId);
                      if (asset) {
                        teamMembersWithBookings[teamMember.id].assets.add(asset.name);
                      }
                    }
                  }
                }
              });
              
              const teamMembersList = Object.values(teamMembersWithBookings);
              
              if (teamMembersList.length === 0) return null;
              
              return (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-blue-900 flex items-center gap-2">
                      <Users className="w-4 h-4" />
                      Filter by TeamMember
                    </h4>
                    {selectedTeamMemberFilters.length > 0 && (
                      <button
                        onClick={() => setSelectedTeamMemberFilters([])}
                        className="text-xs text-blue-600 hover:text-blue-800 underline"
                      >
                        Clear filters
                      </button>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {teamMembersList.map(({ teamMember, tripCount, assets: assetSet }) => {
                      const isSelected = selectedTeamMemberFilters.includes(teamMember.id);
                      const assetNames = Array.from(assetSet);
                      const multipleAssets = assetNames.length > 1;
                      
                      return (
                        <label
                          key={teamMember.id}
                          className={`
                            flex items-center gap-2 px-3 py-2 rounded-lg border-2 cursor-pointer transition-all
                            ${isSelected 
                              ? 'bg-blue-600 text-white border-blue-700' 
                              : 'bg-white text-gray-700 border-gray-300 hover:border-blue-400'}
                            ${multipleAssets ? 'ring-2 ring-orange-400' : ''}
                          `}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedTeamMemberFilters([...selectedTeamMemberFilters, teamMember.id]);
                              } else {
                                setSelectedTeamMemberFilters(selectedTeamMemberFilters.filter(id => id !== teamMember.id));
                              }
                            }}
                            className="w-4 h-4"
                          />
                          <div className="flex flex-col">
                            <span className="font-medium text-sm">{teamMember.name}</span>
                            <span className={`text-xs ${isSelected ? 'text-blue-100' : 'text-gray-500'}`}>
                              {tripCount} trip{tripCount !== 1 ? 's' : ''}
                              {assetNames.length > 0 && ` • ${assetNames.join(', ')}`}
                              {multipleAssets && ' ⚠️'}
                            </span>
                          </div>
                        </label>
                      );
                    })}
                  </div>
                  {selectedTeamMemberFilters.length === 0 && (
                    <p className="text-xs text-blue-700 mt-2">
                      💡 Click teamMember names to filter and review their schedule
                    </p>
                  )}
                </div>
              );
            })()}
            
            {/* Bookings for the day */}
            <div>
              <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                <Package className="w-5 h-5 text-blue-600" />
                Bookings Today ({
                  selectedTeamMemberFilters.length > 0 
                    ? selectedDayBookings.filter(t => selectedTeamMemberFilters.includes(t.teamMemberId)).length
                    : selectedDayBookings.length
                })
              </h3>
              
              {selectedDayBookings.length === 0 ? (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                  <p className="text-gray-500">No trips scheduled for this day</p>
                </div>
              ) : (
                <>
                  {/* Mass Action Toolbar - Same as Team Tab */}
                  {massActionMode && (
                    <MassActionToolbar
                      selectedCount={selectedBookingIds.length}
                      onSelectAll={() => {
                        const allBookingIds = selectedDayBookings.map(t => t.id);
                        setSelectedBookingIds(allBookingIds);
                      }}
                      onDeselectAll={() => setSelectedBookingIds([])}
                      onDelete={() => {
                        // Handle bulk delete if needed
                        toast.info('Bulk delete - coming soon');
                      }}
                      itemName="trip"
                    />
                  )}
                  
                  <div className="space-y-3">
                  {selectedDayBookings
                    .filter(trip => {
                      // Apply teamMember filter if any selected
                      if (selectedTeamMemberFilters.length === 0) return true;
                      return selectedTeamMemberFilters.includes(trip.teamMemberId);
                    })
                    .map(trip => {
                    const statusInfo = getBookingStatusColor(trip);
                    
                    // Get asset and team details for display
                    const asset = assets?.find(b => b.id === trip.assetId);
                    const assignedTeam = trip.teamAssignments?.filter(a => a.teamId) || [];
                    
                    return (
                    <Card key={trip.id} className="relative bg-white hover:shadow-md transition-shadow overflow-hidden">
                      {/* Status-based left border */}
                      <div 
                        className="absolute left-0 top-0 bottom-0"
                        style={{
                          width: '4px',
                          backgroundColor: statusInfo.color
                        }}
                      />
                      
                      <CardContent className="p-4 pl-6">
                        {/* Top: Booking ID + Service (left) + Time/Date/Actions (right) */}
                        <div className="mb-3 pb-3 border-b">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">
                                TRIP ID: <span className="font-mono font-semibold text-blue-600">{trip.confirmationCode || trip.id.slice(0, 8).toUpperCase()}</span>
                              </p>
                              <p className="font-bold text-xl text-gray-900 mb-1">
                                {localStorage.getItem('organizationName') || 'Charter Company'}
                              </p>
                              {trip.serviceName && (
                                <p className="text-sm text-gray-600 mb-2 flex items-center gap-1">
                                  <Package className="w-3.5 h-3.5" />
                                  {trip.serviceName}
                                </p>
                              )}
                              {/* Share Link - boxed */}
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  
                                  // Check if waivers are enabled and configured
                                  const waiversEnabled = localStorage.getItem('waiversEnabled') === 'true';
                                  const waiverText = localStorage.getItem('orgWaiverText');
                                  
                                  if (waiversEnabled && (!waiverText || waiverText.trim().length < 50)) {
                                    toast.error('Please configure your waiver in Operations → Waivers');
                                    return;
                                  }
                                  
                                  try {
                                    // Generate magic link from backend
                                    const bookingId = trip.confirmationCode || trip.id;
                                    const response = await api.post(`/public/bookings/${bookingId}/generate-magic-link`);
                                    
                                    const magicToken = response.data.magicToken;
                                    const magicLink = `${window.location.origin}/customer-ticket/${bookingId}?token=${magicToken}`;
                                    
                                    navigator.clipboard.writeText(magicLink);
                                    toast.success('Ticket link copied!');
                                    setCopiedBookingId(trip.id);
                                    setTimeout(() => setCopiedBookingId(null), 3000);
                                    
                                    // Log that team member copied link
                                    try {
                                      await api.post(`/activity-log`, {
                                        action: 'shared',
                                        resourceType: 'customer_ticket',
                                        resourceId: trip.id,
                                        resourceName: `Ticket ${trip.confirmationCode}`,
                                        details: { method: 'copied_link' }
                                      });
                                    } catch (logError) {
                                      // Ignore logging errors
                                    }
                                  } catch (error) {
                                    console.error('Error generating magic link:', error);
                                    toast.error('Failed to copy link');
                                  }
                                }}
                                className="inline-flex items-center gap-2 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded text-xs font-medium text-blue-700 transition-colors"
                              >
                                {copiedBookingId === trip.id ? (
                                  <>
                                    <Check className="w-3.5 h-3.5 text-green-600" />
                                    <span className="text-green-600">Copied!</span>
                                  </>
                                ) : (
                                  <>
                                    <Link2 className="w-3.5 h-3.5" />
                                    <span>Copy ticket link</span>
                                  </>
                                )}
                              </button>
                            </div>
                            <div className="text-right">
                              <div className="flex items-center gap-2 justify-end mb-1">
                                <Clock className="w-4 h-4 text-gray-500" />
                                <span className="font-bold text-xl text-gray-900">
                                  {formatTime(trip.startTime, timeFormat)}
                                </span>
                                {trip.timezone && (
                                  <span className="text-xs font-medium text-blue-600 px-1.5 py-0.5 bg-blue-50 rounded">
                                    {getTimezoneAbbreviation(trip.timezone)}
                                  </span>
                                )}
                              </div>
                              <p className="text-sm text-gray-600">{trip.duration} minutes</p>
                              <p className="text-xs text-gray-500 mt-1">{formatDate(trip.date, dateFormat)}</p>
                              {!readOnly && (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setEditingBooking(trip);
                                    setEditModalOpen(true);
                                    setDayModalOpen(false);
                                  }}
                                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50 mt-2"
                                >
                                  <Edit className="w-4 h-4 mr-1" />
                                  Edit
                                </Button>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Booking Details Grid */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {/* Asset Dropdown */}
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              <Package className="w-3.5 h-3.5" />
                              Asset
                            </div>
                            {!readOnly ? (
                              <Select
                                key={`asset-${trip.id}-${trip.assetId || 'unassigned'}`}
                                value={trip.assetId || 'unassigned'}
                                onValueChange={async (value) => {
                                  // Combine asset + team updates into ONE call
                                  if (value !== 'unassigned') {
                                    const selectedAsset = assets.find(b => b.id === value);
                                    const updates = { assetId: value };
                                    
                                    // Auto-populate team slots based on asset requirements
                                    if (selectedAsset?.teamRequirements) {
                                      const autoTeamSlots = [];
                                      selectedAsset.teamRequirements.forEach(req => {
                                        for (let i = 0; i < req.quantity; i++) {
                                          autoTeamSlots.push({
                                            teamId: '',
                                            teamName: '',
                                            roleId: req.roleId,
                                            roleName: req.roleName
                                          });
                                        }
                                      });
                                      updates.teamAssignments = autoTeamSlots;
                                    }
                                    
                                    await handleUpdateBooking(trip.id, updates);
                                  } else {
                                    // Clear both asset and team when unassigning
                                    await handleUpdateBooking(trip.id, { 
                                      assetId: null, 
                                      teamAssignments: [] 
                                    });
                                  }
                                }}
                              >
                                <SelectTrigger className="h-9">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="unassigned">Unassigned</SelectItem>
                                  {assets.filter(b => b.active).map(asset => {
                                    // Check for time conflicts
                                    const hasConflict = selectedDayBookings.some(t => 
                                      t.id !== trip.id && 
                                      t.assetId === asset.id &&
                                      checkTimeOverlap(trip, t)
                                    );
                                    
                                    return (
                                      <SelectItem 
                                        key={asset.id} 
                                        value={asset.id}
                                        disabled={hasConflict}
                                        className={hasConflict ? 'opacity-50 cursor-not-allowed' : ''}
                                      >
                                        {asset.name}
                                        {hasConflict && ' (Already booked during this time)'}
                                      </SelectItem>
                                    );
                                  })}
                                  <div className="border-t mt-1 pt-1">
                                    <button
                                      onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        window.dispatchEvent(new CustomEvent('navigate', { detail: 'assets' }));
                                      }}
                                      className="w-full text-left px-2 py-1.5 text-sm text-blue-600 hover:bg-blue-50 rounded flex items-center gap-2"
                                    >
                                      <Plus className="w-4 h-4" />
                                      Add Asset
                                    </button>
                                  </div>
                                </SelectContent>
                              </Select>
                            ) : (
                              asset ? (
                                <p className="text-sm font-semibold text-gray-900">{asset.name}</p>
                              ) : (
                                <p className="text-sm text-gray-400 italic">Not assigned</p>
                              )
                            )}
                          </div>
                          
                          {/* Team Assignments */}
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                              <Users className="w-3.5 h-3.5" />
                              Team ({assignedTeam.length})
                            </div>
                            {!readOnly ? (
                              trip.teamAssignments && trip.teamAssignments.length > 0 ? (
                                <div className="space-y-2">
                                  {trip.teamAssignments.slice(0, expandedBookingDetails[trip.id] ? undefined : 1).map((assignment, idx) => {
                                    const role = roles.find(r => r.id === assignment.roleId);
                                    const roleColor = role?.color || '#6B7280';
                                    
                                    return (
                                      <div key={idx} className="bg-white border border-gray-200 rounded p-2">
                                        <div className="flex items-center gap-1.5 mb-1.5">
                                          <span 
                                            className="w-2 h-2 rounded-full" 
                                            style={{ backgroundColor: roleColor }}
                                          />
                                          <span className="text-xs font-semibold text-gray-700">
                                            {assignment.roleName || 'Unknown'}
                                          </span>
                                        </div>
                                        
                                        <Select
                                          value={assignment.teamId || 'none'}
                                          onValueChange={(teamId) => {
                                            const updatedAssignments = [...trip.teamAssignments];
                                            
                                            if (teamId === 'none') {
                                              updatedAssignments[idx] = {
                                                ...updatedAssignments[idx],
                                                teamId: '',
                                                teamName: ''
                                              };
                                            } else if (teamId === 'add_new') {
                                              window.dispatchEvent(new CustomEvent('navigate', { detail: 'teamMembers' }));
                                              return;
                                            } else {
                                              const selectedTeam = teamMembers.find(c => c.id === teamId);
                                              updatedAssignments[idx] = {
                                                ...updatedAssignments[idx],
                                                teamId: teamId,
                                                teamName: selectedTeam?.name || ''
                                              };
                                            }
                                            
                                            handleUpdateBooking(trip.id, { teamAssignments: updatedAssignments });
                                          }}
                                        >
                                          <SelectTrigger className="h-8 text-xs">
                                            <SelectValue>
                                              {assignment.teamName || <span className="text-gray-400">Select team...</span>}
                                            </SelectValue>
                                          </SelectTrigger>
                                          <SelectContent>
                                            <SelectItem value="none">
                                              <span className="text-gray-500">Unassigned</span>
                                            </SelectItem>
                                            {teamMembers && teamMembers.length > 0 ? (
                                              teamMembers
                                                .filter(c => c.roleId === assignment.roleId)
                                                .map(team => {
                                                  // Check if team has time conflict
                                                  const hasConflict = selectedDayBookings.some(t => 
                                                    t.id !== trip.id &&
                                                    t.teamAssignments?.some(a => a.teamId === team.id) &&
                                                    checkTimeOverlap(trip, t)
                                                  );
                                                  
                                                  // Check if team is already assigned to THIS trip in another slot
                                                  const alreadyAssigned = trip.teamAssignments.some((a, i) => 
                                                    i !== idx && a.teamId === team.id
                                                  );
                                                  
                                                  const isDisabled = hasConflict || alreadyAssigned;
                                                  const reason = alreadyAssigned ? 'Already assigned to this trip' : 'Already assigned during this time';
                                                  
                                                  return (
                                                    <SelectItem 
                                                      key={team.id} 
                                                      value={team.id}
                                                      disabled={isDisabled}
                                                      className={isDisabled ? 'opacity-50 cursor-not-allowed' : ''}
                                                    >
                                                      {team.name}
                                                      {isDisabled && ` (${reason})`}
                                                    </SelectItem>
                                                  );
                                                })
                                            ) : (
                                              <SelectItem value="none" disabled>No teamMembers</SelectItem>
                                            )}
                                            <div className="border-t mt-1 pt-1">
                                              <SelectItem value="add_new" className="text-blue-600 hover:bg-blue-50">
                                                <span className="flex items-center gap-2">
                                                  <Plus className="w-4 h-4" />
                                                  Add Team
                                                </span>
                                              </SelectItem>
                                            </div>
                                          </SelectContent>
                                        </Select>
                                      </div>
                                    );
                                  })}
                                  {trip.teamAssignments.length > 1 && (
                                    <button
                                      onClick={() => {
                                        setExpandedBookingDetails(prev => ({
                                          ...prev,
                                          [trip.id]: !prev[trip.id]
                                        }));
                                      }}
                                      className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1"
                                    >
                                      {expandedBookingDetails[trip.id] ? 'Show less' : `Show ${trip.teamAssignments.length - 1} more team`}
                                      <ChevronDown className={`w-3 h-3 transition-transform ${expandedBookingDetails[trip.id] ? 'rotate-180' : ''}`} />
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-400 italic">Select asset to add team</p>
                              )
                            ) : (
                              assignedTeam.length > 0 ? (
                                <div className="space-y-2">
                                  {assignedTeam.slice(0, expandedBookingDetails[trip.id] ? undefined : 1).map((team, idx) => {
                                    const role = roles.find(r => r.id === team.roleId);
                                    const teamMember = teamMembers?.find(c => c.id === team.teamId);
                                    return (
                                      <div key={idx} className="bg-gray-50 rounded p-2">
                                        <div className="flex items-center gap-2 mb-1">
                                          <span 
                                            className="w-2 h-2 rounded-full flex-shrink-0" 
                                            style={{ backgroundColor: role?.color || '#6B7280' }}
                                          />
                                          <span className="text-sm font-semibold text-gray-900">
                                            {team.teamName}
                                            <span className="text-xs text-gray-500 ml-1">({team.roleName})</span>
                                          </span>
                                        </div>
                                        {teamMember?.phone && (
                                          <a href={`tel:${teamMember.phone}`} className="text-xs text-blue-600 hover:underline ml-4 flex items-center gap-1">
                                            <Phone className="w-3 h-3" />
                                            {teamMember.phone}
                                          </a>
                                        )}
                                      </div>
                                    );
                                  })}
                                  {assignedTeam.length > 1 && (
                                    <button
                                      onClick={() => {
                                        setExpandedBookingDetails(prev => ({
                                          ...prev,
                                          [trip.id]: !prev[trip.id]
                                        }));
                                      }}
                                      className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                                    >
                                      {expandedBookingDetails[trip.id] ? 'Show less' : `Show ${assignedTeam.length - 1} more team`}
                                    </button>
                                  )}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-400 italic">Not assigned</p>
                              )
                            )}
                          </div>
                        </div>
                        
                        {/* Collapsible Customer Details Section */}
                        <div className="mt-4 pt-4 border-t">
                          <div className="flex items-center justify-between mb-3">
                            <button
                              onClick={() => {
                                setExpandedBookingDetails(prev => ({
                                  ...prev,
                                  [trip.id]: !prev[trip.id]
                                }));
                              }}
                              className="flex items-center gap-2 text-sm font-semibold text-gray-700 hover:text-gray-900"
                            >
                              <span>Customer & Sharing Details</span>
                              <div className="w-6 h-6 flex items-center justify-center bg-gray-200 hover:bg-gray-300 rounded-full transition-colors">
                                <ChevronDown className={`w-4 h-4 transition-transform ${expandedBookingDetails[trip.id] ? 'rotate-180' : ''}`} />
                              </div>
                            </button>
                            
                            {/* Waiver Status - only show if enabled */}
                            {localStorage.getItem('waiversEnabled') === 'true' && (
                              trip.waiverSigned ? (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    // Use authenticated endpoint since this is admin view
                                    const response = await api.get(`/bookings/${trip.id}`);
                                    setSelectedWaiverBooking(response.data);
                                  } catch (error) {
                                    console.error('Error loading waiver:', error);
                                    setSelectedWaiverBooking(trip);
                                  }
                                  setShowWaiverDetailsModal(true);
                                }}
                                className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 hover:bg-green-200 border border-green-300 rounded text-xs font-semibold text-green-700 transition-colors"
                              >
                                <Check className="w-3 h-3" />
                                Signed (PIN: {trip.waiverOtp || '---'})
                              </button>
                            ) : (
                              <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 border border-orange-300 rounded text-xs font-semibold text-orange-700">
                                <AlertCircle className="w-3 h-3" />
                                Waiver Pending
                              </span>
                            )
                            )}
                          </div>
                          
                          {expandedBookingDetails[trip.id] && (
                            <div className="mt-3 space-y-3">
                              {/* Customer Info */}
                              <div>
                                <div className="flex items-center gap-2 text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                                  <User className="w-3.5 h-3.5" />
                                  Customer
                                </div>
                                {!readOnly ? (
                                  <div className="space-y-2">
                                    <div className="flex flex-col md:flex-row gap-2">
                                      <Input
                                        value={trip.customerName || ''}
                                        onChange={(e) => handleUpdateBooking(trip.id, { customerName: e.target.value })}
                                        placeholder="Customer name"
                                        className="h-9 flex-1"
                                      />
                                      <Input
                                        value={trip.customerPhone || ''}
                                        onChange={(e) => handleUpdateBooking(trip.id, { customerPhone: e.target.value })}
                                        placeholder="(555) 123-4567"
                                        className="h-9 flex-1"
                                      />
                                    </div>
                                    <Input
                                      value={trip.pickupLocation || ''}
                                      onChange={(e) => handleUpdateBooking(trip.id, { pickupLocation: e.target.value })}
                                      placeholder="Pickup location (e.g., Location Dock 5)"
                                      className="h-9"
                                    />
                                  </div>
                                ) : (
                                  <div className="space-y-2">
                                    <div className="flex flex-col md:flex-row gap-4">
                                      {trip.customerName && (
                                        <p className="text-sm text-gray-900">{formatCustomerName(trip.customerName)}</p>
                                      )}
                                      {trip.customerPhone && (
                                        <a href={`tel:${trip.customerPhone}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1">
                                          <Phone className="w-3 h-3" />
                                          {trip.customerPhone}
                                        </a>
                                      )}
                                    </div>
                                    {trip.pickupLocation && (
                                      <p className="text-sm text-gray-600">📍 {trip.pickupLocation}</p>
                                    )}
                                  </div>
                                )}
                              </div>
                              
                              {/* Team Contact - Privacy: First name + Last initial */}
                              {(() => {
                                const lastTeam = assignedTeam[assignedTeam.length - 1];
                                if (lastTeam) {
                                  const teamData = teamMembers?.find(c => c.id === lastTeam.teamId);
                                  if (teamData?.phone) {
                                    const names = lastTeam.teamName.split(' ');
                                    const firstName = names[0];
                                    const lastInitial = names[names.length - 1]?.[0] || '';
                                    const displayName = `${firstName} ${lastInitial}.`;
                                    
                                    return (
                                      <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                                        <div className="flex items-center gap-2 text-xs font-semibold text-blue-700 uppercase tracking-wide mb-1">
                                          <Users className="w-3.5 h-3.5" />
                                          Team Contact
                                        </div>
                                        <p className="text-sm text-blue-900 font-medium">{displayName}</p>
                                        <a href={`tel:${teamData.phone}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1 font-medium mt-1">
                                          <Phone className="w-3 h-3" />
                                          {teamData.phone}
                                        </a>
                                      </div>
                                    );
                                  }
                                }
                                return null;
                              })()}
                              
                              {/* Emergency Contact - Debounced */}
                              <div className="p-3 bg-orange-50 rounded-lg border border-orange-200">
                                <div className="flex items-center gap-2 text-xs font-semibold text-orange-700 uppercase tracking-wide mb-1">
                                  <AlertCircle className="w-3.5 h-3.5" />
                                  Emergency
                                </div>
                                <p className="text-xs text-orange-900 mb-2">If something happens to the teamMember:</p>
                                {!readOnly ? (
                                  <Input
                                    value={emergencyPhone}
                                    onChange={(e) => {
                                      const newNumber = e.target.value;
                                      setEmergencyPhone(newNumber);
                                      
                                      // Debounce saving to localStorage
                                      if (updateTimeouts['emergency']) {
                                        clearTimeout(updateTimeouts['emergency']);
                                      }
                                      
                                      const timeoutId = setTimeout(() => {
                                        localStorage.setItem('orgEmergencyPhone', newNumber);
                                        toast.success('Emergency number saved!', { duration: 1500 });
                                      }, 1000);
                                      
                                      setUpdateTimeouts(prev => ({ ...prev, emergency: timeoutId }));
                                    }}
                                    placeholder="(512) 555-5555"
                                    className="h-8 text-sm"
                                  />
                                ) : (
                                  <a href={`tel:${emergencyPhone}`} className="text-sm text-blue-600 hover:underline flex items-center gap-1 font-medium">
                                    <Phone className="w-3 h-3" />
                                    {emergencyPhone}
                                  </a>
                                )}
                              </div>
                              
                              {/* TeamMember's Last Location */}
                              {(() => {
                                const teamMember = assignedTeam.find(c => c.roleName?.toLowerCase().includes('teamMember'));
                                if (teamMember && trip.teamMemberLocation) {
                                  const { lat, long, updatedAt } = trip.teamMemberLocation;
                                  const mapsLink = `https://fishing-app.gpsnauticalcharts.com/i-asseting-fishing-web-app/fishing-marine-charts-navigation.html?title=TeamMember+Location#18/${lat}/${long}`;
                                  
                                  return (
                                    <div className="p-2 bg-green-50 rounded border border-green-200 text-xs">
                                      <span className="font-semibold text-green-700">TeamMember's Last Location:</span>
                                      <a 
                                        href={mapsLink}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-blue-600 hover:underline ml-1"
                                      >
                                        View on Map
                                      </a>
                                      <span className="text-green-600 ml-1">
                                        (Updated {new Date(updatedAt).toLocaleTimeString()})
                                      </span>
                                      <p className="text-gray-500 mt-0.5 italic">*Not all teamMembers update their location accordingly</p>
                                    </div>
                                  );
                                }
                                return null;
                              })()}
                              
                              {/* Delete Button */}
                              {!readOnly && (
                                <Button
                                  onClick={() => {
                                    setBookingToDelete(trip);
                                    setShowDeleteConfirm(true);
                                  }}
                                  variant="outline"
                                  className="w-full border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                                >
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete Booking
                                </Button>
                              )}
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  );
                  })}
                </div>
                </>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-Scheduler Warnings Dialog */}
      <AutoSchedulerWarningsDialog
        open={warningsDialogOpen}
        onOpenChange={setWarningsDialogOpen}
        result={schedulerResult}
      />

      {/* Add Booking Wizard - Clean 3-Step Flow */}
      {addBookingModalOpen && (
        <AddBookingWizard
          assets={assets}
          teamMembers={teamMembers}
          roles={roles}
          onComplete={async (tripData) => {
            const result = await handleAddBooking(tripData);
            return result; // Return booking data - modal will be closed by success modal
          }}
          onCancel={() => setAddBookingModalOpen(false)}
        />
      )}

      {/* Delete Confirmation Modal */}
      {/* Bulk Import Modal */}
      <BulkImportDialog
        open={bulkImportOpen}
        onOpenChange={setBulkImportOpen}
        onSuccess={() => {
          setBulkImportOpen(false);
          fetchData();
          if (onRefresh) onRefresh();
          toast.success('Bookings imported successfully!');
        }}
      />

      {/* Mass Delete Confirmation Dialog */}
      <Dialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm Deletion</DialogTitle>
            <DialogDescription>
              {tripToDelete 
                ? `Are you sure you want to delete the trip at ${formatTime(tripToDelete.startTime, timeFormat)} on ${formatDate(tripToDelete.date, dateFormat)}?`
                : `Are you sure you want to delete ${selectedBookingIds.length} trip(s)?`
              } This action cannot be undone.
            </DialogDescription>
          </DialogHeader>
          <div className="flex gap-2 justify-end mt-4">
            <Button variant="outline" onClick={() => {
              setShowDeleteConfirm(false);
              setBookingToDelete(null);
            }}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={() => {
              if (tripToDelete) {
                handleDeleteSingleBooking(tripToDelete.id);
                setBookingToDelete(null);
              } else {
                handleBulkDelete();
              }
              setShowDeleteConfirm(false);
            }}>
              Delete {tripToDelete ? 'Booking' : `${selectedBookingIds.length} Booking(s)`}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Auto-Scheduler Confirmation Modal */}
      <Dialog open={showSchedulerConfirm} onOpenChange={setShowSchedulerConfirm}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Rocket className="w-6 h-6 text-purple-600" />
              🚀 Ready to Auto-Schedule?
            </DialogTitle>
            <DialogDescription>
              This will automatically assign teamMembers and assets to unassigned trips. Let's make some magic happen!
            </DialogDescription>
          </DialogHeader>
          
          {schedulerPreview && (
            <div className="space-y-4 mt-4">
              {/* Preview Info */}
              <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-lg p-4 border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2 flex items-center gap-2">
                  <Sparkles className="w-4 h-4" />
                  Schedule Preview
                </h3>
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-600">Date Range:</span>
                    <p className="font-medium">{formatDate(schedulerPreview.startDate, dateFormat)} - {formatDate(schedulerPreview.endDate, dateFormat)}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Unassigned Bookings:</span>
                    <p className="font-medium text-orange-600">{schedulerPreview.trips.length}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Available TeamMembers:</span>
                    <p className="font-medium text-blue-600">{schedulerPreview.teamMembers.length}</p>
                  </div>
                  <div>
                    <span className="text-gray-600">Available Assets:</span>
                    <p className="font-medium text-green-600">{schedulerPreview.assets.length}</p>
                  </div>
                </div>
              </div>

              {/* Fun Loading Animation */}
              {isScheduling && (
                <div className="text-center py-8">
                  <div className="relative">
                    <Loader2 className="w-12 h-12 mx-auto text-purple-600 animate-spin" />
                    <div className="mt-4 space-y-2">
                      <p className="text-lg font-semibold text-purple-800">🎯 Working the magic...</p>
                      <p className="text-sm text-gray-600 animate-pulse">Matching teamMembers and assets like a boss!</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              {!isScheduling && (
                <div className="flex gap-3 pt-4">
                  <Button
                    onClick={executeAutoSchedule}
                    className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-semibold py-3"
                    disabled={isScheduling}
                  >
                    <Zap className="w-5 h-5 mr-2" />
                    🚀 Let's Do This!
                  </Button>
                  <Button
                    onClick={() => setShowSchedulerConfirm(false)}
                    variant="outline"
                    className="px-6"
                    disabled={isScheduling}
                  >
                    Maybe Later
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Health Check Modal */}
      <Dialog open={healthModalOpen} onOpenChange={setHealthModalOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Activity className="w-5 h-5 text-blue-600" />
              Schedule Health Check
            </DialogTitle>
            <DialogDescription>
              Checking for scheduling conflicts and issues
            </DialogDescription>
          </DialogHeader>
          
          {healthLoading ? (
            <div className="py-8 text-center">
              <Loader2 className="w-8 h-8 animate-spin mx-auto text-blue-600 mb-4" />
              <p className="text-gray-600">Analyzing schedule...</p>
            </div>
          ) : healthData?.error ? (
            <div className="py-8 text-center text-red-600">
              <AlertCircle className="w-12 h-12 mx-auto mb-4" />
              <p>{healthData.error}</p>
            </div>
          ) : healthData ? (
            <div className="space-y-4">
              {/* Overall Status */}
              <div className={`p-4 rounded-lg ${
                (healthData.asset_conflicts?.length === 0 && healthData.teamMember_conflicts?.length === 0)
                  ? 'bg-green-50 border-2 border-green-200'
                  : 'bg-red-50 border-2 border-red-200'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="font-semibold text-lg">
                      {(healthData.asset_conflicts?.length === 0 && healthData.teamMember_conflicts?.length === 0)
                        ? '✅ All Clear!'
                        : '⚠️ Issues Found'}
                    </h3>
                    <p className="text-sm text-gray-600 mt-1">
                      Checked {healthData.trip_count || 0} trips for today
                    </p>
                  </div>
                  <div className="text-right">
                    <div className="text-2xl font-bold">
                      {(healthData.asset_conflicts?.length || 0) + (healthData.teamMember_conflicts?.length || 0)}
                    </div>
                    <div className="text-xs text-gray-500">Conflicts</div>
                  </div>
                </div>
              </div>

              {/* Asset Conflicts */}
              {healthData.asset_conflicts && healthData.asset_conflicts.length > 0 && (
                <div>
                  <h4 className="font-semibold text-red-600 mb-2 flex items-center gap-2">
                    <Package className="w-4 h-4" />
                    Asset Conflicts ({healthData.asset_conflicts.length})
                  </h4>
                  <div className="space-y-2">
                    {healthData.asset_conflicts.map((conflict, idx) => (
                      <div key={idx} className="p-3 bg-red-50 rounded border border-red-200">
                        <p className="font-medium text-red-900">{conflict.asset_name}</p>
                        <p className="text-sm text-gray-700 mt-1">
                          Double-booked: {conflict.overlapping_trips?.map(t => 
                            `${t.time || 'N/A'} - ${t.end_time || 'N/A'}`
                          ).join(' & ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* TeamMember Conflicts */}
              {healthData.teamMember_conflicts && healthData.teamMember_conflicts.length > 0 && (
                <div>
                  <h4 className="font-semibold text-orange-600 mb-2 flex items-center gap-2">
                    <Users className="w-4 h-4" />
                    TeamMember Conflicts ({healthData.teamMember_conflicts.length})
                  </h4>
                  <div className="space-y-2">
                    {healthData.teamMember_conflicts.map((conflict, idx) => (
                      <div key={idx} className="p-3 bg-orange-50 rounded border border-orange-200">
                        <p className="font-medium text-orange-900">{conflict.teamMember_name}</p>
                        <p className="text-sm text-gray-700 mt-1">
                          Double-booked: {conflict.overlapping_trips?.map(t => 
                            `${t.time || 'N/A'} - ${t.end_time || 'N/A'}`
                          ).join(' & ')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* No Conflicts Message */}
              {(!healthData.asset_conflicts || healthData.asset_conflicts.length === 0) &&
               (!healthData.teamMember_conflicts || healthData.teamMember_conflicts.length === 0) && (
                <div className="text-center py-6">
                  <Check className="w-16 h-16 mx-auto text-green-600 mb-3" />
                  <p className="text-lg font-semibold text-green-900">Perfect Schedule!</p>
                  <p className="text-gray-600 mt-1">No conflicts detected in your schedule</p>
                </div>
              )}

              <Button 
                onClick={() => setHealthModalOpen(false)} 
                className="w-full"
                variant="outline"
              >
                Close
              </Button>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
      
      {/* Edit Booking Modal */}
      <EditBookingModal
        isOpen={editModalOpen}
        onClose={() => {
          setEditModalOpen(false);
          setEditingBooking(null);
        }}
        trip={editingBooking}
        assets={assets}
        teamMembers={teamMembers}
        roles={roles}
        allBookings={trips}
        onSave={async (tripId, updatedData) => {
          await handleUpdateBooking(tripId, updatedData);
          onRefresh();
        }}
        timeFormat={timeFormat}
      />
      
      {/* Waiver Details Modal - Simple Operational View */}
      <Dialog open={showWaiverDetailsModal} onOpenChange={setShowWaiverDetailsModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Waiver Signatures ({selectedWaiverBooking?.waiverSignatures?.length || 1})</DialogTitle>
            <DialogDescription>
              Who's signed the waiver for this trip
            </DialogDescription>
          </DialogHeader>
          
          {selectedWaiverBooking && (
            <div className="mt-4 space-y-3">
              {/* Show all signatures - Simple view */}
              {(selectedWaiverBooking.waiverSignatures || [selectedWaiverBooking.waiverRecord]).filter(Boolean).map((sig, idx) => (
                <div key={idx} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                  <div className="flex items-center justify-between mb-3">
                    <h3 className="font-semibold text-gray-900">Signature #{idx + 1}</h3>
                    <div className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      sig.verificationMethod === 'sms' ? 'bg-green-100 text-green-800' : 'bg-orange-100 text-orange-800'
                    }`}>
                      {sig.verificationMethod === 'sms' ? '✓ SMS Verified' : 'ID Required at Asset'}
                    </div>
                  </div>
                  
                  {/* Simple operational info only */}
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-0.5">Name</label>
                      <p className="text-gray-900">{sig.signatureName || 'Unknown'}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-0.5">Verification PIN</label>
                      <p className="font-mono font-bold text-green-600 text-lg">{sig.pin || 'N/A'}</p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-0.5">Signed At</label>
                      <p className="text-gray-900">
                        {sig.signedAt ? new Date(sig.signedAt).toLocaleString() : 'Unknown'}
                      </p>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-gray-600 mb-0.5">Method</label>
                      <p className="text-gray-900">
                        {sig.verificationMethod === 'sms' ? 'SMS Verified' : 'ID Check Required'}
                      </p>
                    </div>
                  </div>
                  
                  {sig.verificationMethod !== 'sms' && (
                    <div className="mt-3 p-2 bg-orange-50 border border-orange-200 rounded text-xs text-orange-800">
                      ⚠️ TeamMember must verify government ID at asset before departure
                    </div>
                  )}
                </div>
              ))}
              
              {/* Booking Info */}
              <div className="border-t border-gray-200 pt-4 mt-4">
                <div className="bg-blue-50 p-3 rounded">
                  <p className="text-xs font-semibold text-blue-900">Booking: {selectedWaiverBooking.confirmationCode || selectedWaiverBooking.id}</p>
                  <p className="text-xs text-blue-700">
                    {new Date(selectedWaiverBooking.date).toLocaleDateString()} at {selectedWaiverBooking.startTime}
                  </p>
                </div>
              </div>
              
              <Button
                onClick={() => setShowWaiverDetailsModal(false)}
                className="w-full"
                variant="outline"
              >
                Close
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      </div>
    </DashboardPageLayout>
  );
};

export default CalendarScheduler;
